// src/pages/mlb/MLBOptimizer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, v));

const num = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).replace(/\$/g, "").replace(/[,  \s]/g, "").replace(/%/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const fmt0 = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");

const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "—");

const geoMeanPct = (items, accessor) => {
  const eps = 1e-6;

  const vals = (items || [])
    .map((x) => Number(accessor(x)))
    .filter((v) => Number.isFinite(v))
    .map((v) => Math.max(eps, Math.min(100, v)));

  if (!vals.length) return 0;

  const logAvg = vals.reduce((sum, v) => sum + Math.log(v), 0) / vals.length;
  return Math.exp(logAvg);
};

function getStackString(players, rowsByName) {
  const teamCounts = new Map();

  for (const name of players || []) {
    const r = rowsByName.get(name);
    if (!r || r.isPitcher) continue; // hitters only

    const t = r.team;
    if (!t) continue;

    teamCounts.set(t, (teamCounts.get(t) || 0) + 1);
  }

  const counts = Array.from(teamCounts.values()).sort((a, b) => b - a);

  if (!counts.length) return "0-0";

  const primary = counts[0] || 0;
  const secondary = counts[1] || 0;

  return `${primary}-${secondary}`;
}

const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};


// ✅ ADD IT RIGHT HERE
function parsePctRangeInput(s) {
  const raw = String(s || "").trim();
  if (!raw) return { min: null, max: null };

  const cleaned = raw.replace(/%/g, "").replace(/\s+/g, "");
  const parts = cleaned.split(/[-–—]/).filter(Boolean);

  const toNum = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
  };

  if (parts.length === 1) {
    const mx = toNum(parts[0]);
    return { min: null, max: mx == null ? null : clamp(mx, 0, 100) };
  }

  const a = toNum(parts[0]);
  const b = toNum(parts[1]);

  if (a == null || b == null) return { min: null, max: null };

  return {
    min: clamp(Math.min(a, b), 0, 100),
    max: clamp(Math.max(a, b), 0, 100),
  };
}

/* ----------------------------- UI THEME ----------------------------- */
const cx = (...classes) => classes.filter(Boolean).join(" ");

const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
};

// small design tokens (shared)
const UI = {
  card: "rounded-2xl border shadow-sm",
  pill:
    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]",
  btn: {
    primary: "h-9 px-4 rounded-md font-semibold",
    ghost: "h-9 px-3 rounded-md border text-sm font-semibold",
    icon: "inline-flex items-center justify-center w-9 h-9 rounded-md border",
  },
  inputBase:
    "h-9 px-3 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-sky-300/40 focus:border-sky-400",
};

const pct = (v) => {
  if (v == null) return 0;
  const s = String(v).replace(/[%\s]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0;
};
const normTeam = (s) => (s || "").toUpperCase().trim();
const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/* ----------------------------- time helpers ----------------------------- */
// Parse common MLB start-time strings (e.g. "7:05 PM", "1:10PM", "19:05", "2026-03-30 7:05 PM") into minutes since midnight.
// Returns NaN if it can't parse.
function parseMlbTimeToMinutes(timeStr) {
  const s0 = String(timeStr || "").trim();
  if (!s0) return NaN;

  // Try to pull "H:MM AM/PM" from any larger string
  const m = s0.match(/(\b\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)?\b/i);
  if (m) {
    let h = Number(m[1]);
    const mi = Number(m[2]);
    const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi;
  }

  // Try "HHMM" or "HMM" like 705p? (rare)
  const m2 = s0.match(/\b(\d{3,4})\b/);
  if (m2) {
    const raw = m2[1];
    const h = raw.length === 3 ? Number(raw.slice(0, 1)) : Number(raw.slice(0, 2));
    const mi = raw.length === 3 ? Number(raw.slice(1)) : Number(raw.slice(2));
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi;
  }

  return NaN;
}
function formatMlbTimeShort(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return "";
  const m = s.match(/(\b\d{1,2}\s*:\s*\d{2}\s*(?:AM|PM)?\b)/i);
  return (m ? m[1] : s).replace(/\s+/g, " ").trim();
}


function formatMlbTimeET(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return "";

  // If already has AM/PM, keep it and just normalize spacing + add ET
  const m12 = s.match(/\b(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)\b/i);
  if (m12) {
    const hh = Number(m12[1]);
    const mm = m12[2];
    const ap = m12[3].toUpperCase();
    return `${hh}:${mm} ${ap} ET`;
  }

  // 24h "HH:MM(:SS)?" (like "19:07:00")
  const m24 = s.match(/\b(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*\d{2})?\b/);
  if (m24) {
    let h = Number(m24[1]);
    const mi = m24[2];
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mi} ${ap} ET`;
  }

  // Fallback: attempt to extract a time-ish token and return it
  return s;
}

/* ------------------ NEW last-updated helpers (via meta.json) ------------------ */
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }
  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }
  return null;
}
function useLastUpdatedFromSource(sourceUrl) {
  const metaUrl = useMemo(() => toMetaUrl(sourceUrl), [sourceUrl]);
  const [text, setText] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const meta = await r.json();
        const d = parseMetaToDate(meta);
        if (!d) return;
        const t = d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        if (alive) setText(t);
      } catch {}
    })();
    return () => { alive = false; };
  }, [metaUrl]);
  return text;
}

/* ------------------------------ data ------------------------------- */
const SOURCE = "/data/mlb/latest/projections.json";
const SITE_IDS_SOURCE = "/data/mlb/latest/site_ids.json";

/* ----------------------------- sites ------------------------------- */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    slots: [
      { name: "P1",   eligible: ["P"] },
      { name: "P2",   eligible: ["P"] },
      { name: "C",    eligible: ["C"] },
      { name: "1B",   eligible: ["1B"] },
      { name: "2B",   eligible: ["2B"] },
      { name: "3B",   eligible: ["3B"] },
      { name: "SS",   eligible: ["SS"] },
      { name: "OF1",  eligible: ["OF"] },
      { name: "OF2",  eligible: ["OF"] },
      { name: "OF3",  eligible: ["OF"] },
    ],
    pown: ["DK pOWN%","DK pOWN"],
    opt:  ["DK Opt%","DK Opt"],
    salKey: "DK Sal",
    projKey: "DK Proj",
    floorKey: "DK Floor",
    ceilKey: "DK Ceiling",
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 35000,
    slots: [
      { name: "P",    eligible: ["P"] },
      { name: "C/1B", eligible: ["C","1B"] },
      { name: "2B",   eligible: ["2B"] },
      { name: "3B",   eligible: ["3B"] },
      { name: "SS",   eligible: ["SS"] },
      { name: "OF1",  eligible: ["OF"] },
      { name: "OF2",  eligible: ["OF"] },
      { name: "OF3",  eligible: ["OF"] },
      { name: "UTIL", eligible: ["C","1B","2B","3B","SS","OF"] },
    ],
    pown: ["FD pOWN%","FD pOWN"],
    opt:  ["FD Opt%","FD Opt"],
    salKey: "FD Sal",
    projKey: "FD Proj",
    floorKey: "FD Floor",
    ceilKey: "FD Ceiling",
  },
};

/* ----------------------------- team colors ------------------------- */
const TEAM_COLORS = {
  ARI: "#A71930", ATL: "#CE1141", BAL: "#DF4601", BOS: "#BD3039",
  CHC: "#0E3386", CIN: "#C6011F", CLE: "#00385D", COL: "#33006F",
  CWS: "#27251F", DET: "#0C2340", HOU: "#002D62", KC:  "#004687",
  LAA: "#BA0021", LAD: "#005A9C", MIA: "#00A3E0", MIL: "#12284B",
  MIN: "#002B5C", NYM: "#002D72", NYY: "#003087", ATH: "#003831",
  PHI: "#E81828", PIT: "#FDB827", SD:  "#2F241D", SEA: "#0C2C56",
  SF:  "#FD5A1E", STL: "#C41E3A", TB:  "#092C5C", TEX:"#003278",
  TOR:"#134A8E", WSH:"#AB0003",
};
const chipStyle = (abbr, active=false) => {
  const bg = TEAM_COLORS[abbr] || "#e5e7eb";
  const color = "#fff";
  return {
    background: bg, color,
    borderRadius: 6, padding: "2px 8px",
    border: active ? "2px solid rgba(255,255,255,0.8)" : "1px solid rgba(0,0,0,0.05)",
    boxShadow: active ? "0 0 0 2px rgba(0,0,0,0.08) inset" : "none",
  };
};

/* ---------------------- Canonical game key helpers ------------------ */
const gameKey = (a, b) => {
  const A = (a || "").toUpperCase().trim();
  const B = (b || "").toUpperCase().trim();
  return A <= B ? `${A}|${B}` : `${B}|${A}`;
};
const gameLabelFromKey = (key) => {
  const [A, B] = key.split("|");
  return `${A} @ ${B}`;
};

/* ----------------------------- fetchers ---------------------------- */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0); // to allow manual refetch

  const refetch = () => setVersion((v) => v + 1);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);

        const raw = await res.text();
        const cleaned = raw.replace(/^\uFEFF/, "").trim();

        let j;
        try {
          j = cleaned ? JSON.parse(cleaned) : null;
        } catch (e) {
          throw new Error(`Could not parse JSON. Preview: ${cleaned.slice(0, 200)}`);
        }

        if (alive) {
          setData(j);
          setErr(null);
        }
      } catch (e) {
        if (alive) { setData(null); setErr(e); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [url, version]);

  return { data, err, loading, refetch };
}

/* --------------------------- sticky state -------------------------- */
const useStickyState = (key, init) => {
  const read = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return init;
      const parsed = JSON.parse(raw);
      return parsed ?? init;
    } catch {
      return init;
    }
  }, [key, init]);

  // IMPORTANT: initialize from localStorage synchronously (prevents "refresh wipes" in dev)
  const [v, setV] = useState(() => read());

  // If the key changes (site/slate switch), load that namespace
  useEffect(() => {
    setV(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persist after changes
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(v)); } catch {}
  }, [key, v]);

  return [v, setV];
};
// Persist a Set<T> (API mirrors useState for Sets)
const useStickySet = (key, initialArr = []) => {
  const [arr, setArr] = useStickyState(key, initialArr);
  const set = React.useMemo(() => new Set(arr), [arr]);

  const setSet = React.useCallback((updater) => {
    const next =
      typeof updater === "function"
        ? updater(new Set(set))
        : updater;
    setArr(Array.from(next || []));
  }, [setArr, set]);

  return [set, setSet];
};
// Persist a plain object
const useStickyDict = (key, initObj = {}) => useStickyState(key, initObj);

/* --------------------------- SSE wrapper --------------------------- */
async function solveStreamMLB(payload, onItem, onDone) {
  const res = await fetch(`${API_BASE}/solve_mlb_stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!res.ok || !res.body) throw new Error("Stream failed");

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  let doneSeen = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const chunk of parts) {
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try {
        const evt = JSON.parse(line.slice(6));
        if (evt.done) {
          doneSeen = true;
          onDone?.(evt);
        } else {
          onItem?.(evt);
        }
      } catch {}
    }
  }

  if (!doneSeen) onDone?.({ produced: null, reason: "stream-ended" });
}

/* ---------------- CSV + ID helpers (DK/FD) ------------------------- */
function downloadCSV(rows, header, fname) {
  const blob = new Blob([header + "\n" + rows.join("\n")], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fname; a.click();
  URL.revokeObjectURL(url);
}
function dkNameWithId(name, idMap) {
  const id = idMap.dk.get(name);
  return id ? `${name} (${id})` : name;
}
function fdNameWithIdRaw(name, id, prefix, display) {
  const outId = prefix ? `${prefix}-${id}` : id;
  return `${outId}:${display || name}`;
}
function siteNameWithId(site, name, idMap) {
  return site === "dk" ? dkNameWithId(name, idMap) : (idMap.fd.get(name) ? `${idMap.fd.get(name)}:${name}` : name);
}

function getExportIdForPlayer(site, name, siteMeta, slate) {
  const rec =
    siteMeta?.[site]?.byName?.get(name) ||
    siteMeta?.[site]?.byName?.get(normName(name)) ||
    null;

  if (!rec) return "";

  if (slate === "early") return String(rec.early_id || "").trim();
  if (slate === "late") return String(rec.late_id || "").trim();
  return String(rec.id || "").trim();
}
function exportDetailedLineupsCSV({
  lineups,
  site,
  rows,
  siteIds,
  siteMeta,
  slate,
  fname = "mlb_lineups_detailed.csv",
}) {
  if (!Array.isArray(lineups) || !lineups.length) {
    alert("No lineups to export.");
    return;
  }

  const cfg = SITES[site] || SITES.dk;
  const ids = buildIdIndex(siteIds || {});
  const rowsByName = new Map((rows || []).map((r) => [r.name, r]));

  const pick = (row, keys) => {
    for (const k of keys || []) {
      if (row?.[k] != null && String(row[k]).trim() !== "") return row[k];
    }
    return null;
  };

  const headers = [
    "Lineup #",
    "Site",
    "Salary",
    "Total Proj",
    "Total pOWN%",
    "Total Opt%",
    ...slotHeaderNames(site),
  ];

  const lines = lineups.map((L, i) => {
    const ordered = orderedRowsFromLineup(site, L, rowsByName);

let totalPown = 0; // percent (0-100+)
let totalOpt = 0;  // percent (0-100+)

const cells = ordered.map((o) => {
  const name = o?.name;
  if (!name) return "";

  // MLB rows store pown/opt as fractions (ex: 0.12 for 12%)
  totalPown += (num(o?.pown) || 0) * 100;
  totalOpt  += (num(o?.opt)  || 0) * 100;

const exportId = getExportIdForPlayer(site, name, siteMeta, slate);

if (site === "dk") {
  return escapeCSV(exportId ? `${name} (${exportId})` : name);
} else {
  const display = ids.fdDisplay.get(name) || name;
  return escapeCSV(exportId ? fdNameWithIdRaw(name, exportId, ids.fdPrefix, display) : name);
}
});

    while (cells.length < slotHeaderNames(site).length) cells.push("");

    return [
      i + 1,
      site.toUpperCase(),
      L.salary ?? "",
      Number.isFinite(L.total) ? Number(L.total).toFixed(1) : "",
      totalPown.toFixed(1),
      totalOpt.toFixed(1),
      ...cells.slice(0, slotHeaderNames(site).length),
    ].join(",");
  });

  downloadCSV(lines, headers.join(","), fname);
}

/* ===== New: Build site-ID index + FD prefix detection ===== */
function buildIdIndex(siteIds) {
  const out = { dk: new Map(), fd: new Map(), fdDisplay: new Map(), fdPrefix: null };
  const dkList = Array.isArray(siteIds?.dk) ? siteIds.dk : (siteIds?.sites?.dk ?? []);
  const fdList = Array.isArray(siteIds?.fd) ? siteIds.fd : (siteIds?.sites?.fd ?? []);
  for (const r of dkList) {
    const id = String(r.id ?? r.ID ?? r.dk_id ?? r["DK ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    if (id && name) out.dk.set(name, id);
  }
  const counts = new Map();
  for (const r of fdList) {
    const px = r.slateId ?? r.slate_id ?? r.groupId ?? r.group_id ?? r.lid ?? r.prefix ?? null;
    if (px != null && px !== "") counts.set(String(px), (counts.get(String(px)) || 0) + 1);
  }
  if (counts.size === 1) out.fdPrefix = [...counts.keys()][0];
  else if (counts.size > 1) out.fdPrefix = [...counts.entries()].sort((a,b)=>b[1]-a[1])[0][0];

  for (const r of fdList) {
    const id = String(r.id ?? r.ID ?? r.fd_id ?? r["FD ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    const display = String(r.displayName ?? r.Name ?? name).trim();
    if (id && name) {
      out.fd.set(name, id);
      out.fdDisplay.set(name, display);
    }
  }
  return out;
}

/* ===== New: Export CSV (IDs) in *import template* shape ===== */
function slotHeaderNames(site) {
  return site === "dk"
    ? ["P","P","C","1B","2B","3B","SS","OF","OF","OF"]
    : ["P","C/1B","2B","3B","SS","OF","OF","OF","UTIL"];
}

function exportSiteTemplateCSV({
  lineups,
  site,
  rows,
  siteIds,
  siteMeta,
  slate,
  fname = "mlb_site_ids.csv",
}) {
  const ids = buildIdIndex(siteIds);
  const rowsByName = new Map(rows.map(r => [r.name, r]));
  const headers = slotHeaderNames(site);
  const lines = lineups.map((L) => {
    const ordered = orderedRowsFromLineup(site, L, rowsByName);
    const names = ordered.map(o => o?.name).filter(Boolean);
    const cells = names.map((name) => {
  const exportId = getExportIdForPlayer(site, name, siteMeta, slate);

  if (site === "dk") {
    return escapeCSV(exportId ? `${name} (${exportId})` : name);
  } else {
    const display = ids.fdDisplay.get(name) || name;
    return escapeCSV(exportId ? fdNameWithIdRaw(name, exportId, ids.fdPrefix, display) : name);
  }
    });
    while (cells.length < headers.length) cells.push("");
    return cells.slice(0, headers.length).join(",");
  });
  downloadCSV(lines, headers.join(","), fname);
}

/* ---------------------------- page -------------------------------- */
export default function MLBOptimizer({ activeTab = "optimizer", setActiveTab = () => {} }) {

// Theme (match NFL Optimizer look & feel)
const [hideExcluded, setHideExcluded] = useState(false);
const [theme, setTheme] = useStickyState("mlbOpt.theme", "dark");
const isDark = theme === "dark";
const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;

const cardClass = cx(
  "opt-card",
  UI.card,
  isDark
    ? "border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "border-slate-200 bg-slate-50"
);

const ghostBtnClass = cx(
  UI.btn.ghost,
  isDark
    ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
    : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
);

const primaryBtnClass = cx(
  UI.btn.primary,
  "bg-sky-600 text-white hover:bg-sky-700"
);

const iconBtnClass = cx(
  UI.btn.icon,
  isDark
    ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
    : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
);

const inputClass = cx(
  UI.inputBase,
  isDark
    ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
    : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
);

// Shared underline tabs for view, fantasy site, and slate.
const topSegWrap = cx(
  "inline-flex items-center gap-6 border-b",
  isDark ? "border-slate-800" : "border-slate-200"
);

const topSegBtn = (active) =>
  cx(
    "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50",
    active
      ? cx(isDark ? "text-white" : "text-slate-950",
          "after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400")
      : isDark
      ? "text-slate-400 hover:text-slate-100"
      : "text-slate-500 hover:text-slate-900"
  );

const siteSegBtn = topSegBtn;
const slateSegWrap = topSegWrap;
const slateSegBtn = topSegBtn;

  // Projections + Site IDs (data loaders)
  const { data, err, loading, refetch: refetchProj } = useJson(SOURCE);
  const { data: siteIds, refetch: refetchIds } = useJson(SITE_IDS_SOURCE);

  // NEW: last-updated strings from meta.json siblings
  const projUpdatedText = useLastUpdatedFromSource(SOURCE);
  const idsUpdatedText  = useLastUpdatedFromSource(SITE_IDS_SOURCE);

  const [site, setSite] = useStickyState("mlbOpt.site", "dk");
  const cfg = SITES[site];

  // Slate selector (All / Early / Late) — mirrors NFL UX
  const [slate, setSlate] = useStickyState("mlbOpt.slate", "main");

  const [optBy, setOptBy] = useStickyState("mlbOpt.optBy", "proj");
  const [numLineups, setNumLineups] = useStickyState("mlbOpt.N", 20);

  const [maxSalary, setMaxSalary] = useStickyState(`mlbOpt.${site}.cap`, cfg.cap);

  const [globalMax, setGlobalMax] = useStickyState("mlbOpt.gmax", 100);
  const [randomness, setRandomness] = useStickyState("mlbOpt.rand", 0);
  const [lineupPownCap, setLineupPownCap] = useStickyState("mlbOpt.lineupPownCap", "");
  const [geoMeanPownRange, setGeoMeanPownRange] = useStickyState("mlbOpt.geoMeanPownRange", "");

  // Stacks (hitters only). Default DK=5-3, FD=4-4
  const [primaryStack, setPrimaryStack] = useStickyState(`mlbOpt.${site}.stack1`, site === "dk" ? 5 : 4);
  const [secondaryStack, setSecondaryStack] = useStickyState(`mlbOpt.${site}.stack2`, site === "dk" ? 3 : 4);

  // ✅ Allow multiple stack shapes per build (mixed across generated lineups)
  // Stored per-site + per-slate so Early/Late/Main are independent workspaces.
  const defaultStackShapes = React.useMemo(() => (site === "dk" ? ["5-3"] : ["4-4"]), [site]);
  const [stackShapes, setStackShapes] = useStickyState(`mlbOpt.${site}.${slate}.stackShapes`, defaultStackShapes);

  // Ensure there's always at least one shape selected (per workspace)
  useEffect(() => {
    if (!Array.isArray(stackShapes) || stackShapes.length === 0) setStackShapes(defaultStackShapes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, slate]);

  // Ensure FD never exceeds 4
  useEffect(() => {
    if (site === "fd") {
      setPrimaryStack((p) => Math.min(4, Number(p) || 0));
      setSecondaryStack((p) => Math.min(4, Number(p) || 0));
    }
  }, [site]); // eslint-disable-line

  const [avoidHittersVsOppP, setAvoidHittersVsOppP] = useStickyState("mlbOpt.avoidHittersVsOppP", true);
  const [maxHittersVsOppP, setMaxHittersVsOppP] = useStickyState("mlbOpt.maxHittersVsOppP", 0);
  const [minDiff, setMinDiff] = useStickyState("mlbOpt.minDiff", 1);

  // 🔒 persistent constraints (per site)
  const locksKey = React.useMemo(() => `mlbOpt.${site}.${slate}.locks`, [site, slate]);
  const exclsKey = React.useMemo(() => `mlbOpt.${site}.${slate}.excls`, [site, slate]);
  const minKey   = React.useMemo(() => `mlbOpt.${site}.${slate}.minPct`, [site, slate]);
  const maxKey   = React.useMemo(() => `mlbOpt.${site}.${slate}.maxPct`, [site, slate]);
  const boostKey = React.useMemo(() => `mlbOpt.${site}.${slate}.boost`, [site, slate]);

  const [locks, setLocks]   = useStickySet(locksKey, []);
  const [excls, setExcls]   = useStickySet(exclsKey, []);

  // ✅ NEW: exclude toggles (NFL-style) — exclude entire games or teams
  const exGamesKey = React.useMemo(() => `mlbOpt.${site}.${slate}.excludedGames`, [site, slate]);
  const exTeamsKey = React.useMemo(() => `mlbOpt.${site}.${slate}.excludedTeams`, [site, slate]);
  const [excludedGames, setExcludedGames] = useStickySet(exGamesKey, []);
  const [excludedTeams, setExcludedTeams] = useStickySet(exTeamsKey, []);

  const [minPct, setMinPct] = useStickyDict(minKey, {});
  const [maxPct, setMaxPct] = useStickyDict(maxKey, {});
  const [boost, setBoost]   = useStickyDict(boostKey, {});

  const teamBoostKey = React.useMemo(() => `mlbOpt.${site}.${slate}.teamBoost`, [site, slate]);
  const [teamBoost, setTeamBoost] = useStickyDict(teamBoostKey, {});

  // ✅ Custom projection overrides (per site + slate). Blank = use original projection.
  const customProjKey = React.useMemo(() => `mlbOpt.${site}.${slate}.customProj`, [site, slate]);
  const [customProj, setCustomProj] = useStickyDict(customProjKey, {});

  // ✅ Site-ID-only player pool rows are included, but hidden by default.
  const [hideSiteIdOnly, setHideSiteIdOnly] = useStickyState(
    `mlbOpt.${site}.${slate}.hideSiteIdOnly`,
    true
  );

  // ✅ Unprojected/player-pool-only rows can be shown, but stay excluded by default.
  // Users must manually uncheck Excl to allow one into optimizer builds.
  const includedUnprojectedKey = React.useMemo(
    () => `mlbOpt.${site}.${slate}.includedUnprojected`,
    [site, slate]
  );
  const [includedUnprojected, setIncludedUnprojected] = useStickySet(includedUnprojectedKey, []);
  // filters
  const [posFilter, setPosFilter] = useState("ALL");

// store selected game keys (canonical) — persisted per site+slate
const selectedGamesKey = React.useMemo(() => `mlbOpt.${site}.${slate}.selectedGames`, [site, slate]);
const [selectedGamesArr, setSelectedGamesArr] = useStickyState(selectedGamesKey, []);
const selectedGames = useMemo(() => new Set(selectedGamesArr), [selectedGamesArr]);

// Keep the existing Set-based call-sites working (we accept Set | Array | updater(Set)=>Set)
const setSelectedGames = (arg) => {
  setSelectedGamesArr((prevArr) => {
    const prevSet = new Set(prevArr || []);
    let nextSet;
    if (typeof arg === "function") nextSet = arg(prevSet);
    else if (arg instanceof Set) nextSet = arg;
    else if (Array.isArray(arg)) nextSet = new Set(arg);
    else nextSet = new Set();
    return Array.from(nextSet);
  });
};

  const [q, setQ] = useState("");

  // --- Game-time + team totals (from public JSON) ---
  const [batterTimeData, setBatterTimeData] = useState([]);
  const [pitcherTimeData, setPitcherTimeData] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch("/data/mlb/latest/batter_data.json").then((r) => r.json()),
      fetch("/data/mlb/latest/pitcher_data.json").then((r) => r.json()),
    ])
      .then(([bat, pit]) => {
        setBatterTimeData(Array.isArray(bat) ? bat : []);
        setPitcherTimeData(Array.isArray(pit) ? pit : []);
      })
      .catch((e) => {
        console.error("Failed to load mlb time/total json", e);
        setBatterTimeData([]);
        setPitcherTimeData([]);
      });
  }, []);

  const timeByGameKey = useMemo(() => {
    const m = new Map();
    const add = (row) => {
      const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
      const opp = normTeam(row?.Opp ?? row?.opp ?? row?.OPP ?? row?.Opponent);
      const t = row?.Time ?? row?.time ?? row?.Start ?? row?.StartTime ?? row?.["Start Time"] ?? row?.GameTime ?? row?.["Game Time"];
      if (!team || !opp || !t) return;
      const key = gameKey(team, opp);
      const cur = m.get(key);
      const candMin = parseMlbTimeToMinutes(t);
      const curMin = cur ? parseMlbTimeToMinutes(cur) : NaN;
      const better =
        !cur ||
        (Number.isFinite(candMin) && !Number.isFinite(curMin)) ||
        (Number.isFinite(candMin) && Number.isFinite(curMin) && candMin < curMin);
      if (better) m.set(key, String(t));
    };
    batterTimeData.forEach(add);
    pitcherTimeData.forEach(add);
    return m;
  }, [batterTimeData, pitcherTimeData]);

const teamTotalByTeam = useMemo(() => {
  const m = new Map();

  for (const row of batterTimeData || []) {
    const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
    const total = num(row?.Total);

    if (!team || !Number.isFinite(total) || total <= 0) continue;
    m.set(team, total);
  }

  return m;
}, [batterTimeData]);

  // exposure pane pos filter
  const [expPosFilter, setExpPosFilter] = useState("ALL");

  // team restriction for stacks
  const [stackTeams, setStackTeams] = useStickyState(`mlbOpt.stackTeams.${site}.${slate}`, []);
  const [onlyUseStackTeams, setOnlyUseStackTeams] = useStickyState(`mlbOpt.onlyUseStackTeams.${site}.${slate}`, false);
  const stackSet = useMemo(() => new Set(stackTeams), [stackTeams]);

  // Global ceiling for every selected hitter stack across the full build.
  // Team-specific values below act as stricter overrides; blank uses this global cap.
  const [globalStackMaxExp, setGlobalStackMaxExp] = useStickyState(
    `mlbOpt.globalStackMaxExp.${site}.${slate}`,
    100
  );

  const [stackTeamMaxExp, setStackTeamMaxExp] = useStickyState(
    `mlbOpt.stackTeamMaxExp.${site}.${slate}`,
    {}
  );

  const [teamExposureSize, setTeamExposureSize] = useStickyState(
  `mlbOpt.${site}.${slate}.teamExposureSize`,
  3
);
  const [lineups, setLineups] = useStickyState(`mlbOpt.${site}.${slate}.lineups`, []);
  const [stopInfo, setStopInfo] = useState(null);
  const [selectedExposurePlayer, setSelectedExposurePlayer] = useState(null);
  const [showBuildAnalysis, setShowBuildAnalysis] = useState(false);
  const [progressActual, setProgressActual] = useState(0);
  const [progressUI,   setProgressUI] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const tickRef = useRef(null);

  // When switching site/slate, treat it as its own workspace:
  // load slate-scoped persisted state (locks/excludes/min/max/builds/lineups/etc are keyed by slate)
  // and reset transient runtime-only bits.
  useEffect(() => {
    setStopInfo(null);
    setProgressActual(0);
    setProgressUI(0);
    setIsOptimizing(false);
    setSelectedExposurePlayer(null);
    setShowBuildAnalysis(false);
  }, [site, slate]);

  // ===== Saved Builds (per site) =====
  const buildsKey = (k) => `mlbOpt.${site}.${slate}.${k}`;
  const [builds, setBuilds] = useStickyState(buildsKey("builds"), []);
  const [activeBuildId, setActiveBuildId] = useStickyState(buildsKey("active"), null);

  function nextBuildNameForSite(siteKey, slateKey) {
    try {
      const raw = localStorage.getItem(`mlbOpt.${siteKey}.${slateKey}.builds`);
      const arr = raw ? JSON.parse(raw) : [];
      const nums = arr
        .map((b) => (b?.name ? String(b.name).match(/^Build\s+(\d+)$/i) : null))
        .filter(Boolean)
        .map((m) => Number(m[1]))
        .filter((n) => Number.isFinite(n));
      const next = nums.length ? Math.max(...nums) + 1 : 1;
      return `Build ${next}`;
    } catch {
      return "Build 1";
    }
  }

  function saveBuild(name, data) {
    const id = Date.now();
    const rec = {
      id,
      name,
      site,
      ts: new Date().toISOString(),
      settings: {
        site,
        optBy,
        numLineups: Math.max(1, Number(numLineups) || 1),
        cap: Math.min(cfg.cap, Number(maxSalary) || cfg.cap),
        globalMax,
        randomness,
        lineupPownCap,
        primaryStack,
        secondaryStack,
        avoidHittersVsOppP,
        maxHittersVsOppP,
        minDiff,
        onlyUseStackTeams,
        stackTeams: Array.isArray(stackTeams) ? stackTeams : [],
        globalStackMaxExp: clamp(Number(globalStackMaxExp) || 100, 0, 100),
        stackTeamMaxExp: { ...(stackTeamMaxExp || {}) },
        locks: Array.from(locks),
        excls: Array.from(excls),
        minPct,
        maxPct,
        boost,
        customProj,
        hideSiteIdOnly,
        includedUnprojected: Array.from(includedUnprojected),
      },
      lineups: data,
    };
    const next = [...builds, rec];
    setBuilds(next);
    setActiveBuildId(id);
  }

  function loadBuild(id) {
    const b = builds.find((x) => x.id === id);
    if (!b) return;
    setActiveBuildId(id);
    setLineups(b.lineups || []);
    setProgressActual((b.lineups || []).length);
    setProgressUI((b.lineups || []).length);

    // (Optional) Restore settings from the build (comment out if you only want lineups)
    const s = b.settings || {};
    if (s.site && s.site !== site) setSite(s.site);
    setOptBy(s.optBy ?? optBy);
    setNumLineups(s.numLineups ?? numLineups);
    setMaxSalary(s.cap ?? maxSalary);
    setGlobalMax(s.globalMax ?? globalMax);
    setRandomness(s.randomness ?? randomness);
    setLineupPownCap(s.lineupPownCap ?? lineupPownCap);
    setPrimaryStack(s.primaryStack ?? primaryStack);
    setSecondaryStack(s.secondaryStack ?? secondaryStack);
    setAvoidHittersVsOppP(!!s.avoidHittersVsOppP);
    setMaxHittersVsOppP(s.maxHittersVsOppP ?? maxHittersVsOppP);
    setMinDiff(s.minDiff ?? minDiff);
    setOnlyUseStackTeams(!!s.onlyUseStackTeams);
    setStackTeams(Array.isArray(s.stackTeams) ? s.stackTeams : []);
    setGlobalStackMaxExp(s.globalStackMaxExp ?? 100);
    setStackTeamMaxExp(s.stackTeamMaxExp || {});
    setLocks(new Set(s.locks || []));
    setExcls(new Set(s.excls || []));
    setMinPct(s.minPct || {});
    setMaxPct(s.maxPct || {});
    setBoost(s.boost || {});
    setCustomProj(s.customProj || {});
    setHideSiteIdOnly(s.hideSiteIdOnly ?? true);
    setIncludedUnprojected(new Set(s.includedUnprojected || []));
  }

  function renameBuild(id, newName) {
    setBuilds((B) => B.map((b) => (b.id === id ? { ...b, name: newName || b.name } : b)));
  }

  function deleteBuild(id) {
    setBuilds((B) => B.filter((b) => b.id !== id));
    if (activeBuildId === id) {
      setActiveBuildId(null);
      setLineups([]);
      setProgressActual(0);
      setProgressUI(0);
    }
  }
  function clearBuilds() {
    const ok = window.confirm ? window.confirm("Clear all saved builds for this site?") : true;
    if (!ok) return;
    setBuilds([]);
    setActiveBuildId(null);
    setLineups([]);
    setProgressActual(0);
    setProgressUI(0);
  }


  useEffect(() => {
    if (!isOptimizing) return;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setProgressUI((p) => {
        const N = Math.max(1, Number(numLineups) || 1);
        const target = Math.max(progressActual, 1);
        const ceiling = Math.max(0, N - 1);
        return Math.min(Math.max(p + 1, target), ceiling);
      });
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [isOptimizing, progressActual, numLineups]);

  useEffect(() => {
    // On site switch, don't clear constraints (they're per-site); just reset run UI
    setLineups([]); setStopInfo(null); setProgressActual(0); setProgressUI(0); setIsOptimizing(false);
  }, [site]);

  // Build ID maps for quick CSV (non-template)
  const idMap = useMemo(() => {
    const m = { dk: new Map(), fd: new Map() };
    const rows = siteIds || {};
    for (const row of (rows?.dk || rows?.sites?.dk || [])) m.dk.set(row.name, String(row.id));
    for (const row of (rows?.fd || rows?.sites?.fd || [])) m.fd.set(row.name, String(row.id));
    return m;
  }, [siteIds]);

  // --- Site meta (pos/salary/id) pulled from site_ids.json ---
const siteMeta = useMemo(() => {
  const out = { dk: { byName: new Map(), byId: new Map() }, fd: { byName: new Map(), byId: new Map() } };

  const dkRows = Array.isArray(siteIds?.dk) ? siteIds.dk : (siteIds?.sites?.dk ?? []);
  const fdRows = Array.isArray(siteIds?.fd) ? siteIds.fd : (siteIds?.sites?.fd ?? []);

  const pick = (row, keys) => {
    for (const k of keys) {
      if (row?.[k] != null && String(row[k]).trim() !== "") return row[k];
    }
    return null;
  };

  const normalizeSitePos = (posRaw) => {
    const txt = String(posRaw || "").toUpperCase();
    if (!txt) return "";
    return txt
      .replace(/\bSP\b/g, "P")
      .replace(/\bRP\b/g, "P")
      .replace(/[;,|]/g, "/")
      .replace(/\s+/g, "")
      .trim();
  };

  const ingest = (siteKey, arr) => {
    for (const r of arr) {
      const name = String(pick(r, ["name","player","Player","Name"]) || "").trim();
      const id   = String(pick(r, ["id","ID","dk_id","fd_id","site_id"]) || "").trim();
      const pos  = normalizeSitePos(pick(r, ["pos","Pos","position","Position","rosterPosition"]) || "");
      const sal  = num(pick(r, ["salary","Salary","sal"]) || 0);

      if (!name) continue;

 const rec = {
  name,

  id: String(pick(r, ["id","ID","dk_id","fd_id","site_id"]) || "").trim() || null,
  salary: num(pick(r, ["salary","Salary","sal"]) || 0),

  early_id: String(
    pick(r, ["early_id", "earlyId", "EARLY_ID", "early_site_id"]) || ""
  ).trim() || null,
  early_salary: num(
    pick(r, ["early_salary", "earlySalary", "EARLY_SALARY"]) || 0
  ),

  late_id: String(
    pick(r, ["late_id", "lateId", "LATE_ID", "late_site_id"]) || ""
  ).trim() || null,
  late_salary: num(
    pick(r, ["late_salary", "lateSalary", "LATE_SALARY"]) || 0
  ),

  pos,
  team: normTeam(pick(r, ["team","Team","Tm","TEAM","team_abbr","TeamAbbrev"]) || ""),
  opp: normTeam(pick(r, ["opp","Opp","OPP","opponent","Opponent"]) || ""),
  time: String(pick(r, ["Time", "time", "Start", "Start Time", "StartTime", "Game Time", "GameTime", "ET"]) || "").trim(),
  battingOrder: String(pick(r, ["battingOrder", "BattingOrder", "bo", "BO", "lineupOrder", "LineupOrder", "order", "Order", "batting_order", "LineupSpot", "spot"]) || "").trim(),
};

      out[siteKey].byName.set(name, rec);
      out[siteKey].byName.set(normName(name), rec);
      if (rec.id) out[siteKey].byId.set(rec.id, rec);
      if (rec.early_id) out[siteKey].byId.set(rec.early_id, rec);
      if (rec.late_id) out[siteKey].byId.set(rec.late_id, rec);
    }
  };

  ingest("dk", dkRows);
  ingest("fd", fdRows);

  return out;
}, [siteIds]);

  // --- robust position parsing & pitcher detection ---
  const POS_KEYS = [
    "pos","Pos","POS","Position",
    "DK Pos","FD Pos","POS_DK","POS_FD",
    "dk_pos","fd_pos",
  ];

function rawPositionsFromRow(r, siteKey) {
  const vals = [];

  // Site-specific first
  if (siteKey === "dk") {
    if (r["DK Pos"] != null) vals.push(String(r["DK Pos"]));
    if (r.dk_pos != null) vals.push(String(r.dk_pos));
  } else {
    if (r["FD Pos"] != null) vals.push(String(r["FD Pos"]));
    if (r.fd_pos != null) vals.push(String(r.fd_pos));
  }

  // Generic fallbacks
  for (const k of ["pos","Pos","POS","Position"]) {
    if (r[k] != null && String(r[k]).trim() !== "") vals.push(String(r[k]));
  }

  return vals.join("/");
}

  function normalizeEligible(raw) {
    const txt = String(raw || "").toUpperCase();
    if (!txt) return [];
    const parts = txt
      .split(/[\/,;|]+/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(p => (p === "SP" || p === "RP") ? "P" : p);
    return Array.from(new Set(parts));
  }

  function pitcherish(r) {
    const flags = ["isPitcher","Pitcher","is_p","is_pitcher","PITCHER","p"].some(k => r[k] === true || r[k] === 1 || String(r[k]).toLowerCase() === "true");
    if (flags) return true;
    const statKeys = ["IP","ip","GS","ERA","era","FIP","xFIP","SIERA","K/9","BB/9","KBB","WHIP","pitch_count","PitchCount","pitcher_team","starting_pitcher","is_sp","probable_pitcher"];
    if (statKeys.some(k => r[k] != null)) return true;
    const posTxt = (r.PositionText || r.Role || "").toString().toUpperCase();
    if (/\b(SP|RP|PITCH|STARTER|RELIEF)\b/.test(posTxt)) return true;
    return false;
  }

  function getSlateIdAndSalary(meta, slate) {
  if (!meta) return { id: "", salary: 0 };

  if (slate === "early") {
    return {
      id: String(meta.early_id || "").trim(),
      salary: num(meta.early_salary),
    };
  }

  if (slate === "late") {
    return {
      id: String(meta.late_id || "").trim(),
      salary: num(meta.late_salary),
    };
  }

  return {
    id: String(meta.id || "").trim(),
    salary: num(meta.salary),
  };
}

function getSlateTags(meta) {
  if (!meta) return [];

  const tags = [];

  if (String(meta.id || "").trim() && num(meta.salary) > 0) tags.push("main");
  if (String(meta.early_id || "").trim() && num(meta.early_salary) > 0) tags.push("early");
  if (String(meta.late_id || "").trim() && num(meta.late_salary) > 0) tags.push("late");

  return tags;
}

/* ------------------------------ rows ------------------------------ */
const rowsAll = useMemo(() => {
  if (!data) return [];

  const arr = Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.players)
    ? data.players
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
    ? data
    : [];

  const siteKey = cfg.key;
  const SALARY_RENDER_MAX = 20000;

  const mapped = arr.map((r) => {
    if (!r || typeof r !== "object") return null;

    const name =
      r.player ?? r.Player ?? r.Name ?? r.playerName ?? r.name ?? "";

    if (!String(name).trim()) return null;

    const projIdRaw =
      r[`${siteKey}_id`] ??
      r[`${siteKey}Id`] ??
      r[`${siteKey.toUpperCase()} ID`] ??
      r[`${siteKey.toUpperCase()}_ID`] ??
      r.id ??
      r.ID ??
      "";

    const projId = String(projIdRaw ?? "").trim();

    const metaById = projId ? siteMeta?.[siteKey]?.byId?.get(projId) : null;

    const meta =
      metaById ||
      siteMeta?.[siteKey]?.byName?.get(name) ||
      siteMeta?.[siteKey]?.byName?.get(normName(name)) ||
      null;

    const slates = getSlateTags(meta);
    const slateInfo = getSlateIdAndSalary(meta, slate);

    const rawPosJoined =
      meta?.pos && String(meta.pos).trim() !== ""
        ? String(meta.pos)
        : rawPositionsFromRow(r, siteKey);

    let eligible = normalizeEligible(rawPosJoined);

    if (eligible.length === 0 && pitcherish(r)) {
      eligible = ["P"];
    }

    eligible = normalizeEligible(eligible.join("/"));
    if (!eligible.length) return null;

    const team = normTeam(
      r.team ??
        r.Team ??
        r.Tm ??
        r.TEAM ??
        r.team_abbr ??
        r.TeamAbbrev ??
        ""
    );

    const opp = normTeam(
      r.opp ??
        r.Opp ??
        r.OPP ??
        r.opponent ??
        r.Opponent ??
        ""
    );

    const rawTime =
      r.Time ??
      r["Time ET"] ??
      r["Start"] ??
      r["Start Time"] ??
      r["StartTime"] ??
      r["Game Time"] ??
      r["GameTime"] ??
      r["ET"] ??
      r.time ??
      r.start_time ??
      r.startTime ??
      r.game_time ??
      r.GameStart ??
      r["GameStart"] ??
      "";

    const timeFromJson = team && opp ? timeByGameKey.get(gameKey(team, opp)) : "";
    const timeFinal = timeFromJson || rawTime || "";

    const salaryFromRow = num(
      r[`${siteKey}_sal`] ??
        r[SITES[siteKey].salKey] ??
        r.Salary ??
        r.salary
    );

    const salary =
      num(slateInfo?.salary) > 0
        ? num(slateInfo.salary)
        : salaryFromRow > 0
        ? salaryFromRow
        : num(meta?.salary);

    if (salary <= 0 || salary > SALARY_RENDER_MAX) return null;

    const proj = num(
      r[`${siteKey}_proj`] ??
        r[SITES[siteKey].projKey] ??
        r.Projection ??
        r.Points
    );

    const floor = num(
      r[`${siteKey}_floor`] ??
        r[SITES[siteKey].floorKey] ??
        r.Floor
    );

    const ceil = num(
      r[`${siteKey}_ceil`] ??
        r[SITES[siteKey].ceilKey] ??
        r.Ceiling
    );

    const pown = pct(
      r[`${siteKey}_pown`] ??
        r[SITES[siteKey].pown?.[0]] ??
        r[SITES[siteKey].pown?.[1]]
    );

    const opt = pct(
      r[`${siteKey}_opt`] ??
        r[SITES[siteKey].opt?.[0]] ??
        r[SITES[siteKey].opt?.[1]]
    );

    const isPitcher = eligible.includes("P");
    const val = Number.isFinite(proj) && salary > 0 ? (proj / salary) * 1000 : 0;

    const battingOrderRaw =
      r.battingOrder ??
      r.BattingOrder ??
      r.bo ??
      r.BO ??
      r.lineupOrder ??
      r.LineupOrder ??
      r.order ??
      r.Order ??
      r.batting_order ??
      r.LineupSpot ??
      r.spot ??
      null;

    const battingOrder =
      isPitcher
        ? "-"
        : (battingOrderRaw != null && String(battingOrderRaw).trim() !== ""
            ? String(battingOrderRaw).trim()
            : "-");

    return {
      name: String(name).trim(),
      team,
      opp,
      eligible,
      isPitcher,
      battingOrder,
      salary,
      proj,
      floor,
      ceil,
      pown,
      opt,
      val,
      time: timeFinal,
      game: team && opp ? gameKey(team, opp) : "",
      slates,
      site_id_main: String(meta?.id || "").trim(),
      site_id_early: String(meta?.early_id || "").trim(),
      site_id_late: String(meta?.late_id || "").trim(),
      __raw: r,
    };
  });

  const projectedRows = mapped.filter((r) => r && r.name && Array.isArray(r.eligible) && r.eligible.length);

  // Add players that exist in the site's ID pool but do not have a projection row.
  // These rows are hidden by default in the UI, but can be shown and manually projected.
  const seenNames = new Set(projectedRows.map((r) => normName(r.name)));
  const seenIds = new Set(
    projectedRows
      .flatMap((r) => [r.site_id_main, r.site_id_early, r.site_id_late])
      .map((x) => String(x || '').trim())
      .filter(Boolean)
  );

  const uniqueMetaRows = [];
  const metaSeen = new Set();
  for (const rec of siteMeta?.[siteKey]?.byName?.values?.() || []) {
    if (!rec?.name) continue;
    const slateInfo = getSlateIdAndSalary(rec, slate);
    const slateId = String(slateInfo?.id || '').trim();
    const salary = num(slateInfo?.salary) > 0 ? num(slateInfo.salary) : num(rec.salary);
    const metaKey = slateId || normName(rec.name);
    if (!metaKey || metaSeen.has(metaKey)) continue;
    metaSeen.add(metaKey);
    if (seenNames.has(normName(rec.name)) || (slateId && seenIds.has(slateId))) continue;
    if (!slateId || salary <= 0 || salary > SALARY_RENDER_MAX) continue;

    const eligible = normalizeEligible(rec.pos);
    if (!eligible.length) continue;

    const isPitcher = eligible.includes('P');
    const team = normTeam(rec.team);
    const opp = normTeam(rec.opp);
    const timeFromJson = team && opp ? timeByGameKey.get(gameKey(team, opp)) : '';
    const timeFinal = timeFromJson || rec.time || '';
    const battingOrder = isPitcher ? '-' : (rec.battingOrder || '-');

    uniqueMetaRows.push({
      name: String(rec.name).trim(),
      team,
      opp,
      eligible,
      isPitcher,
      battingOrder,
      salary,
      proj: 0,
      baseProj: 0,
      floor: 0,
      ceil: 0,
      pown: 0,
      opt: 0,
      val: 0,
      time: timeFinal,
      game: team && opp ? gameKey(team, opp) : '',
      slates: getSlateTags(rec),
      site_id_main: String(rec.id || '').trim(),
      site_id_early: String(rec.early_id || '').trim(),
      site_id_late: String(rec.late_id || '').trim(),
      hasProjection: false,
      siteIdOnly: true,
      __raw: rec,
    });
  }

  return [...projectedRows.map((r) => ({ ...r, baseProj: r.proj, hasProjection: true, siteIdOnly: false })), ...uniqueMetaRows];
}, [data, cfg, timeByGameKey, siteMeta, slate]);

  
  /* ----------------------------- slate & games ----------------------------- */
  // MLB: support All / Early / Late slate selection (based on start time when available)
  const SLATE_CUTOFF_MIN = 19 * 60; // 7:00 PM local/ET-ish (good default split)
  const gamesAll = useMemo(() => {
    const best = new Map(); // key -> { key, label, time, kick, teams:[A,B] }
    for (const r of rowsAll) {
      if (!r.team || !r.opp) continue;
      const key = r.game || gameKey(r.team, r.opp);
      const A = r.team;
      const B = r.opp;
      const label = `${A} @ ${B}`;
      const time = formatMlbTimeET(r.time);
      const kick = parseMlbTimeToMinutes(time);

      const prev = best.get(key);
      if (!prev) {
        best.set(key, { key, label, time, kick, teams: [A, B] });
      } else {
        // keep earliest/most-informative time
        const prevKick = prev.kick;
        const better =
          (Number.isFinite(kick) && !Number.isFinite(prevKick)) ||
          (Number.isFinite(kick) && Number.isFinite(prevKick) && kick < prevKick);
        if (better) best.set(key, { key, label, time, kick, teams: [A, B] });
      }
    }
    return Array.from(best.values()).sort((a, b) => {
      const ak = Number.isFinite(a.kick) ? a.kick : 1e9;
      const bk = Number.isFinite(b.kick) ? b.kick : 1e9;
      return ak - bk || a.label.localeCompare(b.label);
    });
  }, [rowsAll]);

  const gameKickByKey = useMemo(() => {
    const m = new Map();
    for (const g of gamesAll) m.set(g.key, g.kick);
    return m;
  }, [gamesAll]);

const rows = useMemo(() => {
  return rowsAll.filter((r) => {
    const slates = Array.isArray(r.slates) ? r.slates : [];

    // If we have explicit slate tags from site_ids, honor them
    if (slates.length > 0) {
      return slates.includes(slate);
    }

    // No site_ids slate match -> keep player on MAIN only
    return slate === "main";
  });
}, [rowsAll, slate]);

const rowsWithBoost = useMemo(() => {
  return rows.map((r) => {
    const playerSteps = Number(boost[r.name] || 0);
    const teamSteps = !r.isPitcher ? Number(teamBoost[r.team] || 0) : 0;
    const totalSteps = playerSteps + teamSteps;

    const rawCustom = customProj?.[r.name];
    const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
    const manualProj = hasCustomProj ? Math.max(0, num(rawCustom)) : null;
    const activeProj = hasCustomProj ? manualProj : (r.baseProj ?? r.proj ?? 0);

    const boostedProj =
      activeProj * (1 + 0.075 * playerSteps + 0.10 * teamSteps);

    const boostedVal =
      Number.isFinite(boostedProj) && r.salary > 0
        ? (boostedProj / r.salary) * 1000
        : 0;

    return {
      ...r,
      proj: activeProj,
      customProj: rawCustom ?? "",
      hasCustomProj,
      boostedProj,
      boostedVal,
      boostSteps: totalSteps,
      playerBoostSteps: playerSteps,
      teamBoostSteps: teamSteps,
    };
  });
}, [rows, boost, teamBoost, customProj]);

  // Games shown in chips should track the current slate (so Select all / Clear applies to what you see)
const allGames = useMemo(() => {
  const best = new Map();

  for (const r of rows) {
    if (!r.team || !r.opp) continue;

    const key = r.game || gameKey(r.team, r.opp);
    const label = `${r.team} @ ${r.opp}`;
    const time = formatMlbTimeET(r.time);
    const kick = parseMlbTimeToMinutes(time);

    const prev = best.get(key);

    if (!prev) {
      best.set(key, {
        key,
        label,
        time,
        kick: Number.isFinite(kick) ? kick : Number.MAX_SAFE_INTEGER,
        teams: [r.team, r.opp],
      });
      continue;
    }

    const prevKick = Number.isFinite(prev.kick) ? prev.kick : Number.MAX_SAFE_INTEGER;
    const nextKick = Number.isFinite(kick) ? kick : Number.MAX_SAFE_INTEGER;

    if (nextKick < prevKick) {
      best.set(key, {
        key,
        label,
        time,
        kick: nextKick,
        teams: [r.team, r.opp],
      });
    }
  }

  return Array.from(best.values()).sort((a, b) => {
    if (a.kick !== b.kick) return a.kick - b.kick;
    return a.label.localeCompare(b.label);
  });
}, [rows]);

  /* ----------------------- order / filter / sort -------------------- */
  const [order, setOrder] = useState([]);
  const sortRef = useRef({ col: "proj", dir: "desc" });

  useEffect(() => {
    const initial = [...rows].sort((a, b) => b.proj - a.proj || a.name.localeCompare(b.name));
    setOrder(initial.map((r) => r.name));
  }, [site, rows.length]); // eslint-disable-line

  const usagePct = useMemo(() => {
    if (!lineups.length) return {};
    const m = new Map();
    for (const L of lineups) for (const p of L.players) m.set(p, (m.get(p) || 0) + 1);
    const total = Math.max(1, lineups.length);
    const out = {};
    for (const [name, cnt] of m.entries()) out[name] = (cnt / total) * 100;
    return out;
  }, [lineups]);

    const rowsByName = useMemo(
  () => new Map(rowsWithBoost.map((r) => [r.name, r])),
  [rowsWithBoost]
);

const getChosenForLineup = React.useCallback(
  (L) => {
    const names = Array.isArray(L?.players) ? L.players : [];
    return names.map((n) => rowsByName.get(n)).filter(Boolean);
  },
  [rowsByName]
);

const selectedPlayerPairings = useMemo(() => {
  if (!selectedExposurePlayer || !lineups.length) return [];

  const counts = new Map();
  let lineupCount = 0;

  for (const L of lineups) {
    const players = Array.isArray(L.players) ? L.players : [];
    if (!players.includes(selectedExposurePlayer)) continue;

    lineupCount += 1;

    for (const p of players) {
      if (p === selectedExposurePlayer) continue;
      counts.set(p, (counts.get(p) || 0) + 1);
    }
  }

  return [...counts.entries()]
    .map(([player, count]) => ({
      player,
      count,
      pct: lineupCount > 0 ? (count / lineupCount) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count || b.pct - a.pct || a.player.localeCompare(b.player));
}, [selectedExposurePlayer, lineups]);

const buildAnalysis = useMemo(() => {
  if (!lineups.length) {
    return {
      avgSalaryUsed: 0,
      avgSalaryRemaining: 0,
      avgProjection: 0,
      avgLineupPown: 0,
      avgGeoMeanPown: 0,
      avgStackCount: 0,
      avgPrimaryStackSize: 0,
      avgSecondaryStackSize: 0,
      topPlayers: [],
      topPairs: [],
      topTriples: [],
      topTeamCombos: [],
      topPrimaryTeams: [],
    };
  }

  const playerCounts = new Map();
  const pairCounts = new Map();
  const tripleCounts = new Map();
  const teamComboCounts = new Map();
  const primaryTeamCounts = new Map();

  let totalSalary = 0;
  let totalProjection = 0;
  let totalLineupPown = 0;
  let totalGeoMeanPown = 0;
  let totalStackCount = 0;
  let totalPrimaryStackSize = 0;
  let totalSecondaryStackSize = 0;

  const makeCombos = (arr, size) => {
    const out = [];
    const n = arr.length;

    if (size === 2) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          out.push([arr[i], arr[j]]);
        }
      }
    } else if (size === 3) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          for (let k = j + 1; k < n; k++) {
            out.push([arr[i], arr[j], arr[k]]);
          }
        }
      }
    }

    return out;
  };

  for (const L of lineups) {
    const chosen = getChosenForLineup(L);
    const names = chosen.map((r) => r.name).filter(Boolean).sort((a, b) => a.localeCompare(b));

    const lineupPown = chosen.reduce((acc, r) => acc + (r.pown || 0) * 100, 0);
    const geoPown = geoMeanPct(chosen, (r) => (r.pown || 0) * 100);

    totalSalary += Number(L.salary) || 0;
    totalProjection += Number(L.total) || 0;
    totalLineupPown += lineupPown;
    totalGeoMeanPown += geoPown;

    for (const name of names) {
      playerCounts.set(name, (playerCounts.get(name) || 0) + 1);
    }

    for (const combo of makeCombos(names, 2)) {
      const key = combo.join(" | ");
      pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
    }

    for (const combo of makeCombos(names, 3)) {
      const key = combo.join(" | ");
      tripleCounts.set(key, (tripleCounts.get(key) || 0) + 1);
    }

    const hitters = chosen.filter((r) => !r.isPitcher && r.team);
    const teamCounts = new Map();

    for (const h of hitters) {
      teamCounts.set(h.team, (teamCounts.get(h.team) || 0) + 1);
    }

    const sortedTeams = [...teamCounts.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    );

    const stackSizes = sortedTeams.map((x) => x[1]);

    totalStackCount += sortedTeams.length;
    totalPrimaryStackSize += stackSizes[0] || 0;
    totalSecondaryStackSize += stackSizes[1] || 0;

    if (sortedTeams[0]?.[0]) {
      primaryTeamCounts.set(
        sortedTeams[0][0],
        (primaryTeamCounts.get(sortedTeams[0][0]) || 0) + 1
      );
    }

    if (sortedTeams.length >= 2) {
      const comboLabel = `${sortedTeams[0][0]} • ${sortedTeams[1][0]}`;
      teamComboCounts.set(comboLabel, (teamComboCounts.get(comboLabel) || 0) + 1);
    } else if (sortedTeams.length === 1) {
      const comboLabel = `${sortedTeams[0][0]} • —`;
      teamComboCounts.set(comboLabel, (teamComboCounts.get(comboLabel) || 0) + 1);
    }
  }

  const lineupCount = Math.max(1, lineups.length);

  return {
    avgSalaryUsed: totalSalary / lineupCount,
    avgSalaryRemaining: cfg.cap - totalSalary / lineupCount,
    avgProjection: totalProjection / lineupCount,
    avgLineupPown: totalLineupPown / lineupCount,
    avgGeoMeanPown: totalGeoMeanPown / lineupCount,
    avgStackCount: totalStackCount / lineupCount,
    avgPrimaryStackSize: totalPrimaryStackSize / lineupCount,
    avgSecondaryStackSize: totalSecondaryStackSize / lineupCount,

    topPlayers: [...playerCounts.entries()]
      .map(([player, count]) => ({
        player,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.player.localeCompare(b.player))
      .slice(0, 12),

    topPairs: [...pairCounts.entries()]
      .map(([key, count]) => {
        const players = key.split(" | ");
        return {
          players,
          label: players.join(" • "),
          count,
          pct: (count / lineupCount) * 100,
        };
      })
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),

    topTriples: [...tripleCounts.entries()]
      .map(([key, count]) => {
        const players = key.split(" | ");
        return {
          players,
          label: players.join(" • "),
          count,
          pct: (count / lineupCount) * 100,
        };
      })
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),

    topTeamCombos: [...teamComboCounts.entries()]
      .map(([label, count]) => ({
        label,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),

    topPrimaryTeams: [...primaryTeamCounts.entries()]
      .map(([team, count]) => ({
        team,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.team.localeCompare(b.team))
      .slice(0, 12),
  };
}, [lineups, cfg.cap, getChosenForLineup]);

  const [cardsSortBy, setCardsSortBy] = useStickyState(`mlbOpt.${site}.${slate}.cardsSortBy`, "proj");
const [cardsSortDir, setCardsSortDir] = useStickyState(`mlbOpt.${site}.${slate}.cardsSortDir`, "desc");

const lineupsForCards = useMemo(() => {
  const dir = cardsSortDir === "asc" ? 1 : -1;

  const getKey = (L) => {
    const ordered = orderedRowsFromLineup(site, L, rowsByName);

    const totalPown = ordered.reduce((s, r) => s + ((r.pown || 0) * 100), 0);
    const geoMeanPown = geoMeanPct(ordered, (r) => (r.pown || 0) * 100);
const displayedTotalProj = ordered.reduce((s, r) => s + (Number(r.proj) || 0), 0);
    const totalOpt = ordered.reduce((s, r) => s + ((r.opt || 0) * 100), 0);

    if (cardsSortBy === "salary") return Number(L.salary) || 0;
    if (cardsSortBy === "pown") return totalPown || 0;
    if (cardsSortBy === "geo") return geoMeanPown || 0;
    if (cardsSortBy === "opt") return totalOpt || 0;

    return ordered.reduce((s, r) => s + (Number(r.proj) || 0), 0) || Number(L.total) || 0;
  };

  return [...(lineups || [])].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    if (ka < kb) return -1 * dir;
    if (ka > kb) return 1 * dir;
    return 0;
  });
}, [lineups, cardsSortBy, cardsSortDir, site, rowsByName]);

  const posOK = (eligible) => {
    if (posFilter === "ALL") return true;
    if (posFilter === "UTIL") return eligible.some((p) => ["C","1B","2B","3B","SS","OF"].includes(p));
    return eligible.includes(posFilter);
  };
  const displayRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const textOK = (r) =>
      !needle ||
      r.name.toLowerCase().includes(needle) ||
      r.team.toLowerCase().includes(needle) ||
      r.opp.toLowerCase().includes(needle) ||
      r.eligible.join("/").toLowerCase().includes(needle) ||
      String(r.salary).includes(needle);

    const gameOK = (r) => selectedGames.size === 0 || selectedGames.has(gameKey(r.team, r.opp));

    const byName = new Map(rowsWithBoost.map((r) => [r.name, r]));
    const orderSet = new Set(order);

    const ordered = order.map((n) => byName.get(n)).filter(Boolean);
    const others = rowsWithBoost.filter((r) => !orderSet.has(r.name));
    const base = [...ordered, ...others];

    return base.filter(
      (r) =>
        posOK(r.eligible) &&
        textOK(r) &&
        gameOK(r) &&
        (!hideSiteIdOnly || r.hasProjection || r.hasCustomProj)
    );
  }, [rowsWithBoost, order, q, posFilter, selectedGames, hideSiteIdOnly]);

  const sortable = new Set(["team","opp","time","salary","proj","val","floor","ceil","pown","opt","usage","bo"]);
  const setSort = (col) => {
    if (!sortable.has(col)) return;

    const dir =
      sortRef.current.col === col
        ? (sortRef.current.dir === "asc" ? "desc" : "asc")
        : "desc";

    sortRef.current = { col, dir };
    const mult = dir === "asc" ? 1 : -1;

    const sorted = [...displayRows].sort((a, b) => {
      if (["team", "opp"].includes(col)) {
        const va = (a[col] || "").toString().toLowerCase();
        const vb = (b[col] || "").toString().toLowerCase();

        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        return a.name.localeCompare(b.name);
      }

      let va;
      let vb;

      if (col === "time") {
        va = parseMlbTimeToMinutes(a.time);
        vb = parseMlbTimeToMinutes(b.time);

        if (!Number.isFinite(va)) va = 1e9;
        if (!Number.isFinite(vb)) vb = 1e9;
      } else if (col === "bo") {
        va = a.isPitcher ? 99 : Number(a.battingOrder || 99);
        vb = b.isPitcher ? 99 : Number(b.battingOrder || 99);
      } else if (col === "pown" || col === "opt" || col === "usage") {
        va = ((col === "usage" ? usagePct[a.name] : a[col]) || 0) * 100;
        vb = ((col === "usage" ? usagePct[b.name] : b[col]) || 0) * 100;
      } else if (col === "proj") {
        va = a.boostedProj ?? a.proj ?? 0;
        vb = b.boostedProj ?? b.proj ?? 0;
      } else if (col === "val") {
        va = a.boostedVal ?? a.val ?? 0;
        vb = b.boostedVal ?? b.val ?? 0;
      } else {
        va = a[col] ?? 0;
        vb = b[col] ?? 0;
      }

      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;

      return a.name.localeCompare(b.name);
    });

    setOrder(sorted.map((r) => r.name));
  };
  const sortArrow = (key) =>
    sortRef.current.col === key ? (sortRef.current.dir === "asc" ? " ▲" : " ▼") : "";


  /* ---------------------- game/team exclude helpers --------------------- */
  const playersByGame = useMemo(() => {
    const m = new Map(); // gameKey -> Set(names)
    for (const r of rowsWithBoost) {
      if (!r.game) continue;
      if (!m.has(r.game)) m.set(r.game, new Set());
      m.get(r.game).add(r.name);
    }
    return m;
  }, [rowsWithBoost]);

  const playersByTeam = useMemo(() => {
    const m = new Map(); // team -> Set(names)
    for (const r of rowsWithBoost) {
      if (!r.team) continue;
      if (!m.has(r.team)) m.set(r.team, new Set());
      m.get(r.team).add(r.name);
    }
    return m;
  }, [rowsWithBoost]);

  // --- missing togglers used by the Game chips UI ---
const toggleExcludeGame = (gKey) => {
  setExcludedGames((prev) => {
    const next = new Set(prev);
    next.has(gKey) ? next.delete(gKey) : next.add(gKey);
    return next;
  });
};

const toggleExcludeTeam = (teamAbbr) => {
  const t = normTeam(teamAbbr);
  if (!t) return;

  setExcludedTeams((prev) => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });
};

  const autoExcludedNames = useMemo(() => {
    const out = new Set();
    for (const g of excludedGames) {
      const set = playersByGame.get(g);
      if (set) for (const n of set) out.add(n);
    }
    for (const t of excludedTeams) {
      const set = playersByTeam.get(t);
      if (set) for (const n of set) out.add(n);
    }
    return out;
  }, [excludedGames, excludedTeams, playersByGame, playersByTeam]);

  const nonProjectedNames = useMemo(() => {
    return new Set(
      rowsWithBoost
        .filter((r) => r.siteIdOnly && !r.hasProjection)
        .map((r) => r.name)
    );
  }, [rowsWithBoost]);

  const effectiveExcls = useMemo(() => {
    const out = new Set(excls);

    // Game/team auto-excludes always win.
    for (const n of autoExcludedNames) out.add(n);

    // Unprojected/player-pool-only rows are excluded by default, even when shown.
    // Manually unchecking Excl stores the player in includedUnprojected.
    for (const n of nonProjectedNames) {
      if (!includedUnprojected.has(n)) out.add(n);
    }

    return out;
  }, [excls, autoExcludedNames, nonProjectedNames, includedUnprojected]);

/* ----------------------------- actions ---------------------------- */
const bumpBoost = (name, step) =>
  setBoost((m) => ({ ...m, [name]: clamp((m[name] || 0) + step, -6, 6) }));

const bumpTeamBoost = (team, step) =>
  setTeamBoost((m) => ({
    ...m,
    [team]: clamp((m[team] || 0) + step, -6, 6),
  }));

const clearTeamBoosts = () => setTeamBoost({});

const clearStackExposureOverrides = () => {
  setStackTeamMaxExp({});
};

const toggleLock = (name) =>
  setLocks((s) => {
    const n = new Set(s);
    n.has(name) ? n.delete(name) : n.add(name);
    return n;
  });

const toggleExcl = (name) => {
  // Unprojected/player-pool-only players start excluded by default.
  // Their Excl checkbox means: unchecked = manually allow this player.
  if (nonProjectedNames.has(name)) {
    setIncludedUnprojected((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

    // Keep the normal exclude set clean for these default-excluded rows.
    setExcls((s) => {
      const n = new Set(s);
      n.delete(name);
      return n;
    });
    return;
  }

  setExcls((s) => {
    const n = new Set(s);
    n.has(name) ? n.delete(name) : n.add(name);
    return n;
  });
};

const toggleExcludeAllShown = () => {
  setExcls((prev) => {
    const next = new Set(prev);
    const names = poolDisplayRows.map((r) => r.name);
    const allExcluded = names.length > 0 && names.every((n) => next.has(n));

    if (allExcluded) names.forEach((n) => next.delete(n));
    else names.forEach((n) => next.add(n));

    return next;
  });
};

const toggleExcludePosition = () => {
  setExcls((prev) => {
    const next = new Set(prev);

    const names = rows
      .filter((r) =>
        posFilter === "ALL" ? true : (r.eligible || []).includes(posFilter)
      )
      .filter((r) => {
        // still respect selected games filter so toggles feel scoped
        if (selectedGames.size === 0) return true;
        return selectedGames.has(gameKey(r.team, r.opp));
      })
      .map((r) => r.name);

    const allExcluded = names.length > 0 && names.every((n) => next.has(n));
    if (allExcluded) names.forEach((n) => next.delete(n));
    else names.forEach((n) => next.add(n));

    return next;
  });
};

const siteIdOnlyRows = useMemo(
  () => rowsWithBoost.filter((r) => r.siteIdOnly && !r.hasProjection),
  [rowsWithBoost]
);

const fullSiteIdPoolRows = useMemo(
  () => rowsWithBoost.filter((r) => r.name && r.salary > 0 && Array.isArray(r.eligible) && r.eligible.length),
  [rowsWithBoost]
);

const toggleExcludeFullSiteIdPool = () => {
  setExcls((prev) => {
    const next = new Set(prev);
    const names = fullSiteIdPoolRows.map((r) => r.name);
    const allExcluded = names.length > 0 && names.every((n) => next.has(n));
    if (allExcluded) names.forEach((n) => next.delete(n));
    else names.forEach((n) => next.add(n));
    return next;
  });
};

const setPlayerCustomProj = (name, value) => {
  setCustomProj((m) => {
    const next = { ...(m || {}) };
    if (value == null || String(value).trim() === "") delete next[name];
    else next[name] = String(value);
    return next;
  });
};

const resetPlayerCustomProj = (name) => {
  setCustomProj((m) => {
    const next = { ...(m || {}) };
    delete next[name];
    return next;
  });
};

const resetCustomProjections = () => setCustomProj({});

const resetConstraints = () => {
  setLocks(new Set());
  setExcls(new Set());
  setMinPct({});
  setMaxPct({});
  setBoost({});
  setTeamBoost({});
  setCustomProj({});
  setIncludedUnprojected(new Set());
  setStackTeamMaxExp({});
};

const hardResetAll = () => {
  // stop any run
  setIsOptimizing(false);
  clearInterval(tickRef.current);

  // clear saved builds
  setBuilds([]);
  setActiveBuildId(null);

  // clear outputs
  setLineups([]);
  setStopInfo(null);
  setProgressActual(0);
  setProgressUI(0);
  setSelectedExposurePlayer(null);
  setShowBuildAnalysis(false);

  // clear constraints + toggles
  setLocks(new Set());
  setExcls(new Set());
  setMinPct({});
  setMaxPct({});
  setBoost({});
  setTeamBoost({});
  setCustomProj({});
  setIncludedUnprojected(new Set());
  setHideSiteIdOnly(true);
  setExcludedGames(new Set());
  setExcludedTeams(new Set());
  setSelectedGames(new Set());

  // reset optimizer inputs
  setNumLineups(20);
  setMaxSalary(cfg.cap);
  setGlobalMax(100);
  setRandomness(0);
  setGeoMeanPownRange("");
  setLineupPownCap("");

// reset stack / rules
setPrimaryStack(0);
setSecondaryStack(0);
setStackShapes(["0-0"]);
setAvoidHittersVsOppP(true);
setMaxHittersVsOppP(0);
setMinDiff(1);
setOnlyUseStackTeams(false);
setStackTeams([]);
setGlobalStackMaxExp(100); // add this
setStackTeamMaxExp({});

  // reset UI filters
  setQ("");
  setPosFilter("ALL");
  setExpPosFilter("ALL");
};

  /* --------------------------- optimize (SSE) ------------------------ */
  async function optimize() {
    if (!rowsWithBoost.length) return;
    setLineups([]);
setStopInfo(null);
setProgressActual(0);
setProgressUI(0);
setIsOptimizing(true);
setSelectedExposurePlayer(null);

    const N = Math.max(1, Number(numLineups) || 1);
    const capVal = Math.min(cfg.cap, Number(maxSalary) || cfg.cap);

    // limit to selected games (if any)
  const gameFiltered = selectedGames.size
    ? rowsWithBoost.filter((r) => r.game && selectedGames.has(r.game))
    : rowsWithBoost;

    // remove auto-excluded players (games/teams toggles)
    const notAutoExcluded = gameFiltered.filter((r) => !autoExcludedNames.has(r.name));

    // limit hitters to selected stack teams, pitchers always allowed
    const allowedHittersOnly = onlyUseStackTeams && stackSet.size > 0;
    const playerPool = allowedHittersOnly
      ? notAutoExcluded.filter((r) => r.isPitcher || stackSet.has(r.team))
      : notAutoExcluded;

    // Clamp stacks for FD to max 4
    const stack1 = site === "fd" ? Math.min(4, Number(primaryStack) || 0) : Math.max(0, Number(primaryStack) || 0);
    const stack2 = site === "fd" ? Math.min(4, Number(secondaryStack) || 0) : Math.max(0, Number(secondaryStack) || 0);

    const { min: geo_mean_pown_min, max: geo_mean_pown_max } =
      parsePctRangeInput(geoMeanPownRange);

    const cleanedGlobalStackMaxExp = clamp(
      Number(globalStackMaxExp) || 100,
      0,
      100
    );

    const cleanedStackTeamMaxExp = Object.fromEntries(
      Object.entries(stackTeamMaxExp || {})
        .filter(([team, v]) => stackSet.has(team) && String(v ?? "").trim() !== "")
        .map(([team, v]) => [
          team,
          clamp(Number(v) || 0, 0, 100),
        ])
    );

    const payload = {
      site,
      slots: cfg.slots,
      players: playerPool
        .filter((r) => (Number(r.proj) || 0) > 0 || locks.has(r.name))
        .map((r) => ({
          name: r.name, team: r.team, opp: r.opp, eligible: r.eligible,
          salary: Math.round(r.salary || 0),
          proj: r.proj || 0, floor: r.floor || 0, ceil: r.ceil || 0,
          pown: r.pown || 0, opt: r.opt || 0,
        })),
      n: N,
      cap: capVal,
      objective: optBy,
      locks: Array.from(locks),
      excludes: Array.from(effectiveExcls),
      boosts: Object.fromEntries(
        rowsWithBoost.map((r) => {
          const playerSteps = Number(boost[r.name] || 0);
          const teamSteps = !r.isPitcher ? Number(teamBoost[r.team] || 0) : 0;
          return [r.name, playerSteps + teamSteps];
        })
      ),
      randomness: clamp(Number(randomness) || 0, 0, 100),
      global_max_pct: clamp(Number(globalMax) || 100, 0, 100),
      min_pct: Object.fromEntries(
        Object.entries(minPct).map(([k, v]) => [k, clamp(Number(v) || 0, 0, 100)])
      ),
      max_pct: Object.fromEntries(
        Object.entries(maxPct).map(([k, v]) => [k, clamp(Number(v) || 100, 0, 100)])
      ),
      min_diff: Math.max(1, Number(minDiff) || 1),
      time_limit_ms: 1500,
      primary_stack_size: stack1,
      secondary_stack_size: stack2,
      avoid_hitters_vs_opp_pitcher: !!avoidHittersVsOppP,
      max_hitters_vs_opp_pitcher: Math.max(0, Number(maxHittersVsOppP) || 0),
      lineup_pown_max:
        String(lineupPownCap).trim() === ""
          ? null
          : clamp(Number(lineupPownCap) || 0, 0, 500),
      geo_mean_pown_min:
        geo_mean_pown_min === null ? null : clamp(Number(geo_mean_pown_min), 0, 500),
      geo_mean_pown_max:
        geo_mean_pown_max === null ? null : clamp(Number(geo_mean_pown_max), 0, 500),
      min_distinct_teams: site === "fd" ? 3 : 2,
      allowed_teams: onlyUseStackTeams ? Array.from(stackSet) : [],
      stack_teams: stackTeams.length ? stackTeams : [],
      global_stack_max_exposure: cleanedGlobalStackMaxExp,
      stack_team_max_exposure: cleanedStackTeamMaxExp,
    };

    const out = [];

    // Determine which stack shapes are ALLOWED for this run.
    // Backend should decide the actual mix; frontend should NOT split lineup counts by shape.
    const hitterSlots = 8;
    const maxPerTeam = site === "dk" ? 5 : 4;

    const clampShapeForSite = (shape) => {
      const parts = String(shape || "").split("-").map((x) => Number(x));
      let a = parts[0] ?? 0;
      let b = parts[1] ?? 0;

      if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
      a = Math.floor(a);
      b = Math.floor(b);

      // "0-0" = no stack requirement
      if (a === 0 && b === 0) return "0-0";

      // otherwise both sides must be positive
      if (a < 1 || b < 1) return null;

      // canonical ordering (bigger stack first)
      if (a < b) [a, b] = [b, a];

      // per-site cap
      a = Math.min(maxPerTeam, a);
      b = Math.min(maxPerTeam, b);

      // cannot exceed total hitter slots
      if (a + b > hitterSlots) return null;

      return `${a}-${b}`;
    };

    let shapes = (Array.isArray(stackShapes) ? stackShapes : [])
      .map(clampShapeForSite)
      .filter(Boolean);

    if (!shapes.length) {
      shapes = [site === "dk" ? "5-3" : "4-4"];
    }

    // one request only — backend handles allowed_stack_shapes
    const singlePayload = {
      ...payload,
      allowed_stack_shapes: shapes,
    };

    const runStreamOnce = (p) =>
      new Promise((resolve, reject) => {
        solveStreamMLB(
          p,
          (evt) => {
            const L = {
              players: evt.drivers || [],
              assignment: Array.isArray(evt.assignment) ? evt.assignment : [],
              salary: evt.salary,
              total: evt.total,
            };
            out.push(L);
            setLineups((prev) => [...prev, L]);
            setProgressActual(out.length);
          },
          (done) => resolve(done)
        ).catch(reject);
      });

    try {
      await runStreamOnce(singlePayload);

      setProgressActual(out.length || N);
      setProgressUI(out.length || N);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      saveBuild(nextBuildNameForSite(site, slate), out);
    } catch (e) {
      out.length = 0;
      setLineups([]);
      setProgressActual(0);

      const res = await fetch(`${API_BASE}/solve_mlb`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(singlePayload),
      });

      if (!res.ok) {
        alert(`Solve failed: ${await res.text()}`);
        setIsOptimizing(false);
        clearInterval(tickRef.current);
        return;
      }

      const j = await res.json();
      const raw =
        (j.lineups || []).map((L) => ({
          players: L.drivers || [],
          assignment: Array.isArray(L.assignment) ? L.assignment : [],
          salary: L.salary,
          total: L.total,
        })) || [];

      out.push(...raw);
      setLineups(raw);
      setProgressActual(raw.length);
      setProgressUI(raw.length);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      saveBuild(nextBuildNameForSite(site, slate), out);
    }

  }

  /* ------------------------------- UI -------------------------------- */
  const metricLabel =
    optBy === "proj" ? "Proj" : optBy === "floor" ? "Floor" : optBy === "ceil" ? "Ceiling" : optBy === "pown" ? "pOWN%" : "Opt%";

  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";
  const textSz = "text-[12px]";

  const TABLE_COLS = [
    { key: "lock",   label: "Lock" },
    { key: "excl",   label: "Excl" },
    { key: "elig",   label: "Pos" },
    { key: "boosts", label: "Boosts" },
    { key: "bo",     label: "BO", sortable: true },
    { key: "name",   label: "Player" },
    { key: "team",   label: "Tm",   sortable: true },
    { key: "opp",    label: "Opp",  sortable: true },
    { key: "time",   label: "Time", sortable: true },
    { key: "salary", label: "Salary", sortable: true },
    { key: "proj",   label: "Proj",   sortable: true },
    { key: "val",    label: "Val",    sortable: true },
    { key: "floor",  label: "Floor",  sortable: true },
    { key: "ceil",   label: "Ceiling",sortable: true },
    { key: "pown",   label: "pOWN%",  sortable: true },
    { key: "min",    label: "Min%" },
    { key: "max",    label: "Max%" },
    { key: "usage",  label: "Usage%", sortable: true },
  ];

  const poolDisplayRows = displayRows.filter((r) => !hideExcluded || !effectiveExcls.has(r.name));

  return (
    <div
      className={cx("mlb-opt", T.page, "px-5 md:px-8 py-4")}
      style={{
        "--mlb-surface": isDark ? "rgba(2,6,23,0.65)" : "rgba(255,255,255,0.92)",
        "--mlb-surface2": isDark ? "rgba(15,23,42,0.85)" : "rgba(248,250,252,1)",
        "--mlb-border": isDark ? "rgba(51,65,85,0.9)" : "rgba(226,232,240,1)",
        "--mlb-text": isDark ? "rgb(241,245,249)" : "rgb(15,23,42)",
        "--mlb-muted": isDark ? "rgb(148,163,184)" : "rgb(71,85,105)",
        "--mlb-thead": isDark ? "rgba(2,6,23,0.85)" : "rgba(241,245,249,1)",
        "--mlb-rowHover": isDark ? "rgba(56,189,248,0.08)" : "rgba(14,165,233,0.06)",
      }}
    >

<style>{`
  .mlb-opt { color: var(--mlb-text); }
  .mlb-opt .opt-card {
    background: var(--mlb-surface2);
    border-color: var(--mlb-border);
  }

  /* Make legacy gray utilities readable in dark mode */
  .mlb-opt .text-gray-900,
  .mlb-opt .text-gray-800,
  .mlb-opt .text-gray-700 { color: var(--mlb-text) !important; }

  .mlb-opt .text-gray-600,
  .mlb-opt .text-gray-500,
  .mlb-opt .text-gray-400,
  .mlb-opt .text-slate-600,
  .mlb-opt .text-slate-500,
  .mlb-opt .text-slate-400 { color: var(--mlb-muted) !important; }

  .mlb-opt .bg-white,
  .mlb-opt .bg-gray-50,
  .mlb-opt .bg-slate-50 { background-color: var(--mlb-surface2) !important; }

  .mlb-opt .border-gray-200,
  .mlb-opt .border-gray-300,
  .mlb-opt .border-slate-200,
  .mlb-opt .border-slate-300 { border-color: var(--mlb-border) !important; }

  /* Tables */
  .mlb-opt table { width: 100%; border-collapse: collapse; }
  .mlb-opt thead th {
    background: var(--mlb-thead);
    color: var(--mlb-text);
    border-bottom: 1px solid var(--mlb-border);
  }
  .mlb-opt tbody td {
    border-bottom: 1px solid var(--mlb-border);
  }
  .mlb-opt tbody tr:hover td { background: var(--mlb-rowHover); }

  /* Inputs/selects */
  .mlb-opt input, .mlb-opt select, .mlb-opt textarea {
    background: var(--mlb-surface);
    color: var(--mlb-text);
    border-color: var(--mlb-border);
  }
  .mlb-opt input::placeholder, .mlb-opt textarea::placeholder { color: var(--mlb-muted); }

  /* Scrollbars (WebKit) */
  .mlb-opt ::-webkit-scrollbar { height: 10px; width: 10px; }
  .mlb-opt ::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.35); border-radius: 999px; }
  .mlb-opt ::-webkit-scrollbar-track { background: rgba(148,163,184,0.08); }
`}</style>


<div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
  <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
    <h1 className={cx("whitespace-nowrap text-2xl md:text-3xl font-extrabold tracking-tight", T.headerText)}>
      MLB — Optimizer
    </h1>
    <div className={cx("flex flex-wrap items-baseline gap-x-3 text-xs", T.subtext)}>
      <span className="italic">Updated: {projUpdatedText || "—"}</span>
      <span>{rows.length} rows</span>
    </div>
  </div>
  <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
  <div
    className={cx(
      "inline-flex shrink-0 items-center rounded-full border p-1",
      isDark
        ? "border-slate-700/80 bg-slate-900/40"
        : "border-slate-300 bg-white/80"
    )}
  >
    <button
      type="button"
      className={cx(
        "px-3 py-1 text-[11px] font-medium rounded-full transition",
        isDark ? "bg-blue-600 text-white" : "text-slate-600"
      )}
      onClick={() => setTheme("dark")}
      title="Dark mode"
      aria-pressed={isDark}
    >
      Dark
    </button>

    <button
      type="button"
      className={cx(
        "px-3 py-1 text-[11px] font-medium rounded-full transition",
        !isDark ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
      )}
      onClick={() => setTheme("light")}
      title="Light mode"
      aria-pressed={!isDark}
    >
      Light
    </button>
  </div>    <div className="flex shrink-0 items-center gap-2">
      <span className={cx("text-[11px] whitespace-nowrap", T.subtext)}>
        Click here if new slate →
      </span>
      <button
        className="px-4 h-9 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 transition"
        onClick={hardResetAll}
      >
        Reset
      </button>
    </div>  </div>
</div>

<section aria-label="Slate and site" className={cx(cardClass, "mb-3 p-3")}>
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
        <div className={cx(topSegWrap, "shrink-0")}>
          <button
            type="button"
            onClick={() => setActiveTab("optimizer")}
            className={topSegBtn(activeTab === "optimizer")}
          >
            Optimizer
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("lateSwap")}
            className={topSegBtn(activeTab === "lateSwap")}
          >
            Late Swap
          </button>
        </div>

        <div className={cx(topSegWrap, "shrink-0")}>
          {["dk", "fd"].map((s) => (
            <button
              key={s}
              onClick={() => setSite(s)}
              className={siteSegBtn(site === s)}
            >
              <img src={SITES[s].logo} alt="" className="w-4 h-4" />
              <span>{SITES[s].label}</span>
            </button>
          ))}
        </div>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className={cx("flex h-9 items-center pb-2 text-xs font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
          Slate:
        </div>

        <div className={slateSegWrap}>
          {[
            { key: "main", label: "Main" },
            { key: "early", label: "Early" },
            { key: "late", label: "Late" },
          ].map((x) => (
            <button
              key={x.key}
              type="button"
              onClick={() => setSlate(x.key)}
              className={slateSegBtn(slate === x.key)}
              title={
                x.key === "main"
                  ? "Main slate"
                  : x.key === "early"
                  ? "Early slate"
                  : "Late slate"
              }
            >
              {x.label}
            </button>
          ))}
        </div>

        <span className={cx("flex h-9 items-center pb-2 text-xs", T.subtext)}>
          {allGames.length} games
        </span>
      </div>
  </div>
</section>

      {/* controls */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-2">
        <section aria-label="Build settings" className={cx(cardClass, "md:col-span-12 p-3")} >
          <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 items-end">
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Optimize by</label>
          <select className="w-full border rounded-md h-8 px-3 py-0 text-[13px]" value={optBy} onChange={(e) => setOptBy(e.target.value)}>
            <option value="proj">Projection</option>
            <option value="floor">Floor</option>
            <option value="ceil">Ceiling</option>
            <option value="pown">pOWN%</option>
            <option value="opt">Opt%</option>
          </select>
        </div>
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Lineups</label>
          <input className="w-full border rounded-md h-8 px-3 py-0 text-[13px]" value={numLineups} onChange={(e) => setNumLineups(e.target.value)} />
        </div>
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Max salary</label>
          <input className="w-full border rounded-md h-8 px-3 py-0 text-[13px]" value={maxSalary} onChange={(e) => setMaxSalary(e.target.value)} />
        </div>
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Global Max %</label>
          <input className="w-full border rounded-md h-8 px-3 py-0 text-[13px]" value={globalMax} onChange={(e) => setGlobalMax(e.target.value)} />
        </div>
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Randomness %</label>
          <input className="w-full border rounded-md h-8 px-3 py-0 text-[13px]" value={randomness} onChange={(e) => setRandomness(e.target.value)} />
        </div>

        {/* lineup-level cap */}
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Max lineup pOWN% (sum)</label>
          <input
            className="w-full border rounded-md h-8 px-3 py-0 text-[13px]"
            placeholder="—"
            value={lineupPownCap}
            onChange={(e) => setLineupPownCap(e.target.value)}
            title="Reject lineups whose total pOWN% exceeds this value"
          />
        </div>

          {/* geo mean pOWN% cap/range */}
        <div className="min-w-0">
          <label className="block text-[11px] text-gray-600 mb-1">Max geo mean pOWN%</label>
          <input
            className="w-full border rounded-md h-8 px-3 py-0 text-[13px]"
            placeholder="e.g. 25-35"
            value={geoMeanPownRange}
            onChange={(e) => setGeoMeanPownRange(e.target.value)}
            title="Optional: constrain lineup geometric-mean pOWN% (enter MAX like 35, or range like 25-35)"
          />
        </div>

          </div>
        </section>

        {/* Stacks / pitcher rule */}
        <div className="opt-card md:col-span-12 rounded-md border p-2">
          <div className="text-[11px] text-gray-600 mb-1">Stack Shapes (hitters only) & Pitcher Rule</div>

          <div className="flex flex-wrap items-center gap-2 mb-2">

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={avoidHittersVsOppP} onChange={(e) => setAvoidHittersVsOppP(e.target.checked)} />
              Avoid hitters vs opp P
            </label>

            <label className="text-sm">Max hitters vs opp P</label>
            <input
              type="number"
              className="w-16 border rounded-md h-8 px-2 py-0 text-[13px]"
              value={maxHittersVsOppP}
              onChange={(e) => setMaxHittersVsOppP(e.target.value)}
            />

            <label className="text-sm">Min lineup diff</label>
            <input
              type="number"
              className="w-16 border rounded-md h-8 px-2 py-0 text-[13px]"
              value={minDiff}
              onChange={(e) => setMinDiff(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 mb-2">
            <button
              className="h-8 px-3 py-0 text-[12px] leading-none rounded-md border hover:bg-white/5 hover:text-white hover:border-white/20"
              onClick={() => setStackShapes([site === "dk" ? "5-3" : "4-4"])}
            >
              Preset: {site === "dk" ? "5-3" : "4-4"}
            </button>

            <button
              className="h-8 px-3 py-0 text-[12px] leading-none rounded-md border hover:bg-white/5 hover:text-white hover:border-white/20"
              onClick={() => setStackShapes([site === "fd" ? "4-2" : "5-2"])}
            >
              Preset: {site === "fd" ? "4-2-1" : "5-2-1"}
            </button>

            <button
              className="h-8 px-3 py-0 text-[12px] leading-none rounded-md border hover:bg-white/5 hover:text-white hover:border-white/20"
              onClick={() => setStackShapes(["4-3"])}
            >
              Preset: 4-3-1
            </button>
          </div>

          {/* ✅ Multi stack shapes (mix across lineups) */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className={cx("text-[11px] mr-1", isDark ? "text-slate-300/80" : "text-slate-600")}>
              Allowed stack shapes:
            </div>

            {(() => {
const hitterSlots = site === "dk" ? 8 : 8; // both are 8 hitters in your configs
const maxPerTeam = site === "dk" ? 5 : 4;

// Add more shapes here
const all = site === "dk"
  ? ["0-0","5-3","5-2","5-1","4-4","4-3","4-2","4-1","3-3","3-2","3-1","2-2","2-1"]
  : ["0-0","4-4","4-3","4-2","4-1","3-3","3-2","3-1","2-2","2-1"];

const avail = all.filter((s) => {
  const [a, b] = s.split("-").map(Number);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  // allow 0-0 for no stacking
  if (a === 0 && b === 0) return true;
  if (a < 1 || b < 1) return false;
  if (a < b) return false;                 // keep canonical ordering
  if (a > maxPerTeam || b > maxPerTeam) return false;
  if (a + b > hitterSlots) return false;   // can’t stack more hitters than exist
  return true;
});

              return avail.map((s) => {
                const on = (stackShapes || []).includes(s);

                return (
                  <button
                    key={s}
                    type="button"
                    className={cx(
                      "h-7 px-2 py-0 text-[11px] leading-none rounded-md border transition whitespace-nowrap",
                      on
                        ? isDark
                          ? "bg-sky-600/15 border-sky-500/60 text-sky-100"
                          : "bg-sky-100 border-sky-300 text-sky-900"
                        : isDark
                        ? "border-slate-700/60 text-slate-200 hover:bg-white/5"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    )}
                    onClick={() => {
                      setStackShapes((prev) => {
                        const cur = Array.isArray(prev) ? prev : [];
                        if (cur.includes(s)) {
                          const next = cur.filter((x) => x !== s);
                          return next.length ? next : [s]; // never allow empty
                        }
                        return [...cur, s];
                      });

                      // Optional convenience: clicking a shape sets the numeric inputs to that shape
                      const [a, b] = s.split("-").map((x) => Number(x));
                      setPrimaryStack(site === "fd" ? Math.min(4, a) : a);
                      setSecondaryStack(site === "fd" ? Math.min(4, b) : b);
                    }}
                    title="Optimizer will mix these shapes across generated lineups"
                  >
                    {s}
                  </button>
                );
              });
            })()}

            <button
              type="button"
              className={cx(
                "h-7 px-2 py-0 text-[11px] leading-none rounded-md border transition whitespace-nowrap ml-1",
                isDark
                  ? "border-slate-700/60 text-slate-200 hover:bg-white/5"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
              )}
              onClick={() => setStackShapes(["0-0"])}
              title="Reset to default for this site/slate"
            >
              Default
            </button>
          </div>

          <hr className="my-2" />

          {/* stack team limiter */}
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] text-gray-600">Limit to specific teams (optional)</div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyUseStackTeams}
                onChange={(e) => setOnlyUseStackTeams(e.target.checked)}
              />
              Only use selected teams (hitters)
            </label>
          </div>

          <div className="flex flex-wrap gap-3">
            {[...new Set(rows.map((r) => r.team).filter(Boolean))]
              .sort((a, b) => {
                const ta = Number(teamTotalByTeam.get(a) || 0);
                const tb = Number(teamTotalByTeam.get(b) || 0);
                if (tb !== ta) return tb - ta;
                return a.localeCompare(b);
              })
              .map((t) => {
                const active = stackSet.has(t);

                return (
                  <button
                    key={`stack-${t}`}
                    onClick={() => {
                      const arr = new Set(stackSet);

                      if (active) {
                        arr.delete(t);
                        setStackTeams(Array.from(arr));
                        setStackTeamMaxExp((m) => {
                          const next = { ...(m || {}) };
                          delete next[t];
                          return next;
                        });
                      } else {
                        arr.add(t);
                        setStackTeams(Array.from(arr));
                      }
                    }}
                    className="text-[12px]"
                    style={chipStyle(t, active)}
                    title={active ? "Selected for stack pool" : "Add team to stack pool"}
                  >
                    {t}
                    {teamTotalByTeam.get(t) ? (
                      <span className="ml-1 opacity-80">{fmt1(teamTotalByTeam.get(t))}</span>
                    ) : null}
                  </button>
                );
              })}

            <div className="flex gap-2 ml-2">
              <button
                className={cx(
                  "px-2 py-1 text-sm rounded-md border",
                  isDark
                    ? "border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                    : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                )}
                onClick={() => setStackTeams([...new Set(rows.map((r) => r.team).filter(Boolean))])}
              >
                All
              </button>
              <button
                className={cx(
                  "px-2 py-1 text-sm rounded-md border",
                  isDark
                    ? "border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                    : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                )}
                onClick={() => setStackTeams([])}
              >
                Clear
              </button>
            </div>
          </div>

          {stackTeams.length > 0 && (
            <div
              className={cx(
                "mt-3 rounded-lg border p-3",
                isDark
                  ? "border-slate-800/80 bg-slate-950/30"
                  : "border-slate-200 bg-slate-50"
              )}
            >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <div className={cx("text-[11px]", isDark ? "text-slate-400" : "text-slate-600")}>
                  Global stack max exposure
                </div>
                <div className="mt-1 flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={globalStackMaxExp}
                    onChange={(e) => setGlobalStackMaxExp(e.target.value)}
                    placeholder="100"
                    className={cx(
                      "w-20 h-8 rounded-md border px-2 text-center text-[12px]",
                      isDark
                        ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                        : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                    )}
                  />
                  <span className={cx("text-[11px]", isDark ? "text-slate-400" : "text-slate-600")}>
                    %
                  </span>
                </div>
                <div className={cx("mt-1 text-[10px]", isDark ? "text-slate-500" : "text-slate-500")}>
                  Applies when a team value is blank. Any entered team value overrides the global cap.
                </div>
              </div>

              <button
                type="button"
                onClick={clearStackExposureOverrides}
                className={cx(
                  "h-7 px-2 py-0 text-[11px] leading-none rounded-md border transition whitespace-nowrap",
                  isDark
                    ? "border-slate-700/60 text-slate-200 hover:bg-white/5"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
                title="Clear team-specific stack exposure overrides"
              >
                Clear Team Caps
              </button>
            </div>

            <div className={cx("mb-2 text-[11px]", isDark ? "text-slate-400" : "text-slate-600")}>
              Optional team-specific overrides
            </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {[...stackTeams]
                  .sort((a, b) => {
                    const ta = Number(teamTotalByTeam.get(a) || 0);
                    const tb = Number(teamTotalByTeam.get(b) || 0);
                    if (tb !== ta) return tb - ta;
                    return a.localeCompare(b);
                  })
                  .map((t) => (
                    <div
                      key={`stack-max-${t}`}
                      className={cx(
                        "flex items-center justify-between gap-3 rounded-md border px-3 py-2 min-w-0",
                        isDark
                          ? "border-slate-800 bg-slate-900/70"
                          : "border-slate-200 bg-white"
                      )}
                    >
                      <div className="min-w-0">
                        <div
                          className={cx(
                            "text-[12px] font-semibold truncate",
                            isDark ? "text-slate-100" : "text-slate-900"
                          )}
                        >
                          {t}
                          {teamTotalByTeam.get(t) ? (
                            <span
                              className={cx(
                                "ml-1 font-normal",
                                isDark ? "text-slate-400" : "text-slate-600"
                              )}
                            >
                              {fmt1(teamTotalByTeam.get(t))}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <span className={cx("text-[11px]", isDark ? "text-slate-400" : "text-slate-600")}>
                          Max
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={stackTeamMaxExp?.[t] ?? ""}
                          onChange={(e) =>
                            setStackTeamMaxExp((m) => ({
                              ...(m || {}),
                              [t]: e.target.value,
                            }))
                          }
                          placeholder={String(clamp(Number(globalStackMaxExp) || 100, 0, 100))}
                          className={cx(
                            "w-16 h-8 rounded-md border px-2 text-center text-[12px]",
                            isDark
                              ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                              : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                          )}
                        />
                        <span className={cx("text-[11px]", isDark ? "text-slate-400" : "text-slate-600")}>
                          %
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

        </div>

      </div>

      {/* Game cards (compact header controls) */}
<section className={`${cardClass} p-3 mb-3`} aria-label="Games">
  <div className="flex flex-wrap items-center gap-2 mb-3">
    <h2 className="text-sm font-semibold mr-1">Games</h2>
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={() => setSelectedGames(new Set(allGames.map((g) => g.key)))}
  >
    Select all games
  </button>
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={() => setSelectedGames(new Set())}
  >
    Clear games
  </button>
  </div>
  <div className="overflow-x-auto">
        <div className="flex gap-2 min-w-max pb-1">
          {allGames.map(({ key, time }) => {
            const active = selectedGames.has(key);
            const excluded = excludedGames.has(key);

            const [teamA, teamB] = String(key || "").split("|");
            const away = teamA || "";
            const home = teamB || "";

            const awayTotal = teamTotalByTeam.get(away);
            const homeTotal = teamTotalByTeam.get(home);

            const awayExcluded = excludedTeams.has(away);
            const homeExcluded = excludedTeams.has(home);

            const teamLogoSrc = (abbr) =>
              abbr ? `/logos/mlb/${String(abbr).toLowerCase()}.png` : "";

            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() =>
                  setSelectedGames((S) => {
                    const n = new Set(S);
                    active ? n.delete(key) : n.add(key);
                    return n;
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedGames((S) => {
                      const n = new Set(S);
                      active ? n.delete(key) : n.add(key);
                      return n;
                    });
                  }
                }}
                className={cx(
                  "w-[150px] shrink-0 rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer",
                  excluded
                    ? isDark
                      ? "border-red-700/50 bg-red-950/10"
                      : "border-red-300 bg-red-50"
                    : active
                    ? isDark
                      ? "border-sky-500/60 bg-slate-900/95"
                      : "border-sky-300 bg-white shadow-sm"
                    : isDark
                    ? "border-slate-800 bg-slate-900/90 hover:border-slate-700"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
                title={excluded ? "Excluded game (all players removed)" : ""}
              >
                <div
                  className={cx(
                    "px-2 py-1.5 border-b",
                    isDark
                      ? "border-slate-800 bg-slate-800/40"
                      : "border-slate-200 bg-slate-50"
                  )}
                >
                  <div className="grid grid-cols-[16px_1fr_20px] items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGames((S) => {
                          const n = new Set(S);
                          active ? n.delete(key) : n.add(key);
                          return n;
                        });
                      }}
                      className={cx(
                        "w-4 h-4 rounded-[4px] border flex items-center justify-center text-[9px] leading-none",
                        active
                          ? isDark
                            ? "border-sky-400 bg-sky-500/15 text-sky-300"
                            : "border-sky-500 bg-sky-50 text-sky-700"
                          : isDark
                          ? "border-slate-700 text-slate-500"
                          : "border-slate-300 text-slate-400"
                      )}
                      aria-label={active ? "Unselect game" : "Select game"}
                    >
                      {active ? "✓" : ""}
                    </button>

                    <div
                      className={cx(
                        "text-center text-[11px] font-semibold whitespace-nowrap",
                        isDark ? "text-slate-300" : "text-slate-600"
                      )}
                    >
                      {formatMlbTimeET(time) || "—"}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExcludeGame(key);
                      }}
                      className={cx(
                        "w-5 h-5 rounded-md border flex items-center justify-center text-[11px] leading-none",
                        excluded
                          ? "bg-red-600 text-white border-red-700"
                          : isDark
                          ? "border-slate-700 text-slate-400 hover:bg-slate-800"
                          : "border-slate-300 text-slate-500 hover:bg-slate-100"
                      )}
                      title={excluded ? "Un-exclude game" : "Exclude game"}
                      aria-label={excluded ? "Unexclude game" : "Exclude game"}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="px-2 py-2">
                  {[
                    { abbr: away, total: awayTotal, teamExcluded: awayExcluded },
                    { abbr: home, total: homeTotal, teamExcluded: homeExcluded },
                  ].map(({ abbr, total, teamExcluded }, idx) => (
                    <div
                      key={`${key}-${abbr}-${idx}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExcludeTeam(abbr);
                      }}
                      className={cx(
                        "flex items-center justify-between rounded-lg px-2 py-1.5",
                        idx === 0 ? "mb-1.5" : "",
                        teamExcluded
                          ? isDark
                            ? "bg-red-950/20 ring-1 ring-red-800/40"
                            : "bg-red-50 ring-1 ring-red-200"
                          : isDark
                          ? "bg-slate-800/45"
                          : "bg-slate-50"
                      )}
                      title={teamExcluded ? `Un-exclude ${abbr}` : `Exclude ${abbr}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <img
                          src={teamLogoSrc(abbr)}
                          alt={abbr}
                          className="w-5 h-5 object-contain shrink-0"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <span
                          className={cx(
                            "font-semibold text-[11px]",
                            isDark ? "text-slate-100" : "text-slate-900"
                          )}
                        >
                          {abbr}
                        </span>
                      </div>

<div className="ml-auto flex items-center gap-2 shrink-0">
  <div className="flex flex-col items-center justify-center leading-none -space-y-[1px]">
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        bumpTeamBoost(abbr, +1);
      }}
      className={cx(
        "h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition",
        (teamBoost[abbr] || 0) > 0
          ? isDark
            ? "text-emerald-300 bg-emerald-500/10"
            : "text-emerald-700 bg-emerald-50"
          : isDark
          ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
          : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
      )}
      title={`Boost ${abbr} hitters +10%`}
    >
      ▲
    </button>

    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        bumpTeamBoost(abbr, -1);
      }}
      className={cx(
        "h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition",
        (teamBoost[abbr] || 0) < 0
          ? isDark
            ? "text-rose-300 bg-rose-500/10"
            : "text-rose-700 bg-rose-50"
          : isDark
          ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
          : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
      )}
      title={`Reduce ${abbr} hitters -10%`}
    >
      ▼
    </button>
  </div>

  <div className="min-w-[34px] text-right leading-none">
    <div
      className={cx(
        "text-[14px] font-semibold tabular-nums",
        isDark ? "text-slate-200" : "text-slate-700"
      )}
    >
      {total ? fmt1(total) : "—"}
    </div>

    {!!(teamBoost[abbr] || 0) && (
      <div
        className={cx(
          "text-[9px] tabular-nums mt-0.5",
          (teamBoost[abbr] || 0) > 0 ? "text-emerald-500" : "text-rose-500"
        )}
      >
        {(teamBoost[abbr] || 0) > 0 ? `+${teamBoost[abbr]}` : teamBoost[abbr]}
      </div>
    )}
  </div>
</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
</section>

<section className={`${cardClass} p-3 mb-3`} aria-label="Optimize">
        {/* progress + button */}
        <div className="flex flex-wrap items-center gap-3">
          <button className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold" onClick={optimize}>
            {`Optimize ${numLineups}`}
          </button>
          <div className={cx("flex-1 min-w-[100px] max-w-[420px] h-2 rounded-full overflow-hidden", isDark ? "bg-slate-800" : "bg-slate-200")}>
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-300"
              style={{
                width: `${
                  (Math.min(progressUI, Math.max(1, Number(numLineups) || 1)) /
                    Math.max(1, Number(numLineups) || 1)) *
                  100
                }%`,
              }}
            />
          </div>
          <div className="text-sm text-gray-600 min-w-[60px] text-right">
            {progressUI}/{numLineups}
          </div>
        </div>
  <details className={`mt-3 border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
    <div className="mt-3 flex flex-wrap gap-2">
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={() => {
      setLocks(new Set());
    }}
  >
    Clear locks
  </button>
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={() => {
      setBoost({});
      setTeamBoost({});
      setMinPct({});
      setMaxPct({});
      setCustomProj({});
    }}
  >
    Clear boosts/min/max/proj edits
  </button>
    </div>
  </details>
</section>

<section className={`${cardClass} mb-6 overflow-hidden`} aria-label="Player pool">
  <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h2 className="text-sm font-semibold">Player Pool</h2>
      <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>{poolDisplayRows.length.toLocaleString()} players shown</span>
    </div>
    <div className="mb-3 flex flex-wrap items-center gap-3">
      {/* Position tabs */}
      <div className="flex flex-wrap items-center gap-3">
        {["ALL", "P", "C", "1B", "2B", "3B", "SS", "OF", "UTIL"].map((p) => (
          <button
            key={p}
            onClick={() => setPosFilter(p)}
            className={`pb-2 text-xs font-semibold border-b-2 transition-colors ${
              posFilter === p
                ? isDark
                  ? "text-sky-400 border-sky-400"
                  : "text-sky-600 border-sky-400"
                : isDark
                  ? "text-slate-500 border-transparent hover:text-slate-100"
                  : "text-slate-500 border-transparent hover:text-slate-900"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="w-full sm:w-auto sm:ml-auto">
  <input
    className={`${inputClass} w-full sm:w-80`}
    placeholder="Search player / team / pos…"
    value={q}
    onChange={(e) => setQ(e.target.value)}
  />
      </div>
    </div>
    <div className="mb-1 flex flex-wrap items-center gap-2">
  <label className={ghostBtnClass + " inline-flex cursor-pointer items-center gap-2 text-xs"}
    title="Unprojected players are hidden by default. When shown, they still stay excluded until you manually uncheck Excl.">
    <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideSiteIdOnly} onChange={() => setHideSiteIdOnly((v) => !v)} />
    Hide Unprojected
  </label>
  <label className={ghostBtnClass + " inline-flex cursor-pointer items-center gap-2 text-xs"}>
    <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideExcluded} onChange={(e) => setHideExcluded(e.target.checked)} />
    Hide Excluded
  </label>
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={toggleExcludeAllShown}
    title="Toggle exclude for all shown players"
  >
    Exclude all shown (toggle)
  </button>
  <button
    className={ghostBtnClass + " text-xs"}
    onClick={toggleExcludePosition}
    title="Toggle exclude for the current position tab"
  >
    Exclude position ({posFilter}) (toggle)
  </button>
    </div>
  </div>
          {/* Player table */}
          <div
            className="overflow-auto max-h-[70vh]"
          >
            <table
              className={`w-full border-separate text-[12px] min-w-[960px] ${
                isDark ? "text-slate-100" : "text-slate-900"
              }`}
              style={{ borderSpacing: 0 }}
            >
              <thead
                className={`sticky top-0 z-10 shadow-sm ${
                  isDark ? "bg-slate-950" : "bg-gray-50"
                }`}
              >
                <tr>
                  {TABLE_COLS.map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      className={`${header} whitespace-nowrap ${
                        sortable ? "cursor-pointer" : "cursor-default"
                      } select-none ${
                        isDark ? "bg-slate-950 text-slate-100" : "bg-gray-50 text-slate-900"
                      }`}
                      onClick={() => sortable && setSort(key)}
                    >
                      {label}
                      {sortable ? (
                        <span className={`${isDark ? "opacity-70" : "opacity-60"}`}>
                          {sortArrow(key)}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td
                      className={`${cell} ${isDark ? "text-slate-400" : "text-gray-500"}`}
                      colSpan={TABLE_COLS.length}
                    >
                      Loading…
                    </td>
                  </tr>
                )}

                {err && (
                  <tr>
                    <td
                      className={`${cell} ${isDark ? "text-red-400" : "text-red-600"}`}
                      colSpan={TABLE_COLS.length}
                    >
                      Failed to load: {String(err)}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !err &&
                  poolDisplayRows.map((r) => (
                    <tr
                      key={r.name}
                      className={`transition-colors ${
                        isDark
                          ? "odd:bg-slate-950/70 even:bg-slate-900/70 hover:bg-slate-800/80"
                          : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/60"
                      }`}
                    >
                      {/* Lock / Exclude */}
                      <td className={cell}>
                      <input
                        type="checkbox"
                        checked={locks.has(r.name)}
                        onChange={() => toggleLock(r.name)}
                        className="h-4 w-4 accent-[#5d7fe6] cursor-pointer"
                      />
                      </td>
                      <td className={cell}>
                        {(() => {
                          const isAutoExcluded = autoExcludedNames.has(r.name);
                          const isManualExcluded = excls.has(r.name);
                          const isDefaultExcludedUnprojected =
                            r.siteIdOnly && !r.hasProjection && !includedUnprojected.has(r.name);
                          const isExcluded = isAutoExcluded || isManualExcluded || isDefaultExcludedUnprojected;

                          return (
                          <input
                            type="checkbox"
                            checked={isExcluded}
                            onChange={() => {
                              if (isAutoExcluded) return;
                              toggleExcl(r.name);
                            }}
                            className={cx(
                              "h-4 w-4 accent-[#5d7fe6]",
                              isAutoExcluded ? "cursor-not-allowed opacity-100" : "cursor-pointer"
                            )}
                            title={
                              isAutoExcluded
                                ? "Excluded via Game/Team toggle (clear that toggle to re-include)"
                                : isDefaultExcludedUnprojected
                                  ? "Unprojected player: excluded by default. Uncheck to allow."
                                  : "Exclude player"
                            }
                          />
                          );
                        })()}
                      </td>

                      {/* Pos / Boosts */}
                      <td className={cell}>
                        <span className={isDark ? "text-slate-200" : "text-slate-700"}>
                          {r.eligible.join("/")}
                        </span>
                      </td>
                      <td className={cell}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            title="+7.5%"
                            onClick={() => bumpBoost(r.name, +1)}
                          >
                            ▲
                          </button>
                          <span
                            className={`w-5 text-center tabular-nums ${
                              isDark ? "text-slate-200" : "text-slate-800"
                            }`}
                          >
                            {r.boostSteps || 0}
                          </span>
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            title="-5%"
                            onClick={() => bumpBoost(r.name, -1)}
                          >
                            ▼
                          </button>
                        </div>
                      </td>

                      {/* BO / Player / Teams */}
                      <td className={`${cell} whitespace-nowrap tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-700"}>
                          {r.isPitcher ? "-" : (r.battingOrder || "-")}
                        </span>
                      </td>

                      <td className={`${cell} whitespace-nowrap`}>
                        <span className={isDark ? "text-slate-100" : "text-slate-900"}>
                          {r.name}
                        </span>
                        {r.siteIdOnly && !r.hasProjection ? (
                          <span
                            className={cx(
                              "ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                              isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"
                            )}
                            title="In site_ids.json but not projections.json"
                          >
                            ID only
                          </span>
                        ) : null}
                      </td>
                      <td className={cell}>
                        <span
                          className="px-1.5 py-0.5 rounded text-[11px]"
                          style={chipStyle(r.team)}
                        >
                          {r.team || "—"}
                        </span>
                      </td>
                      <td className={cell}>
                        <span
                          className="px-1.5 py-0.5 rounded text-[11px]"
                          style={chipStyle(r.opp)}
                        >
                          {r.opp || "—"}
                        </span>
                      </td>

                      <td className={`${cell} whitespace-nowrap`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-700"}>
                          {formatMlbTimeET(r.time) || "—"}
                        </span>
                      </td>

                      {/* Numbers */}
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {fmt0(r.salary)}
                        </span>
                      </td>
                      <td className={`${cell} tabular-nums`}>
                        <div className="inline-flex items-center justify-center gap-1">
                          <input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            value={
                              r.hasCustomProj
                                ? String(r.customProj ?? "")
                                : fmt1(r.baseProj ?? r.proj)
                            }
                            placeholder={fmt1(r.baseProj ?? r.proj)}
                            onFocus={(e) => e.currentTarget.select()}
                            onChange={(e) => setPlayerCustomProj(r.name, e.target.value)}
                            className={cx(
                              "w-16 rounded px-1.5 py-0.5 text-center outline-none border tabular-nums",
                              r.hasCustomProj && Number(r.proj) > Number(r.baseProj || 0)
                                ? isDark
                                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                                  : "bg-emerald-50 border-emerald-300 text-emerald-700"
                                : r.hasCustomProj && Number(r.proj) < Number(r.baseProj || 0)
                                ? isDark
                                  ? "bg-red-500/10 border-red-500/40 text-red-300"
                                  : "bg-red-50 border-red-300 text-red-700"
                                : isDark
                                ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                                : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                            )}
                            style={
                              r.hasCustomProj && Number(r.proj) > Number(r.baseProj || 0)
                                ? { backgroundColor: isDark ? "rgba(16,185,129,0.18)" : "#ecfdf5", borderColor: isDark ? "#34d399" : "#10b981", color: isDark ? "#a7f3d0" : "#047857" }
                                : r.hasCustomProj && Number(r.proj) < Number(r.baseProj || 0)
                                ? { backgroundColor: isDark ? "rgba(239,68,68,0.18)" : "#fef2f2", borderColor: isDark ? "#f87171" : "#ef4444", color: isDark ? "#fecaca" : "#b91c1c" }
                                : undefined
                            }
                            title={
                              r.hasCustomProj
                                ? `Custom projection. Original: ${fmt1(r.baseProj ?? 0)} | Boosted: ${fmt1(r.boostedProj ?? r.proj)}`
                                : "Click/type to enter a custom projection"
                            }
                          />
                          {r.hasCustomProj ? (
                            <button
                              type="button"
                              onClick={() => resetPlayerCustomProj(r.name)}
                              className={cx(
                                "rounded border px-1 py-0.5 text-[10px] leading-none",
                                isDark
                                  ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                                  : "border-slate-300 text-slate-600 hover:bg-slate-100"
                              )}
                              title="Reset this projection"
                            >
                              ↺
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {fmt1(r.boostedVal ?? r.val)}
                        </span>
                      </td>
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {fmt1(r.floor)}
                        </span>
                      </td>
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {fmt1(r.ceil)}
                        </span>
                      </td>
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {fmt1(r.pown * 100)}
                        </span>
                      </td>

                      {/* Min / Max % */}
                      <td className={cell}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            onClick={() =>
                              setMinPct((m) => ({
                                ...m,
                                [r.name]: Math.max(0, (Number(m[r.name]) || 0) - 5),
                              }))
                            }
                            title="-5%"
                          >
                            –
                          </button>
                          <input
                            className={`w-12 rounded px-1.5 py-0.5 text-center outline-none ${
                              isDark
                                ? "bg-slate-950/70 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-sky-400/30"
                                : "bg-white border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-200"
                            }`}
                            value={String(minPct[r.name] ?? "")}
                            onChange={(e) =>
                              setMinPct((m) => ({ ...m, [r.name]: e.target.value }))
                            }
                            placeholder="—"
                          />
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            onClick={() =>
                              setMinPct((m) => ({
                                ...m,
                                [r.name]: Math.min(100, (Number(m[r.name]) || 0) + 5),
                              }))
                            }
                            title="+7.5%"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      <td className={cell}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            onClick={() =>
                              setMaxPct((m) => ({
                                ...m,
                                [r.name]: Math.max(0, (Number(m[r.name]) || 100) - 5),
                              }))
                            }
                            title="-5%"
                          >
                            –
                          </button>
                          <input
                            className={`w-12 rounded px-1.5 py-0.5 text-center outline-none ${
                              isDark
                                ? "bg-slate-950/70 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-sky-400/30"
                                : "bg-white border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-200"
                            }`}
                            value={String(maxPct[r.name] ?? "")}
                            onChange={(e) =>
                              setMaxPct((m) => ({ ...m, [r.name]: e.target.value }))
                            }
                            placeholder="—"
                          />
                          <button
                            className={`px-1.5 py-0.5 rounded border ${
                              isDark
                                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
                            }`}
                            onClick={() =>
                              setMaxPct((m) => ({
                                ...m,
                                [r.name]: Math.min(100, (Number(m[r.name]) || 100) + 5),
                              }))
                            }
                            title="+7.5%"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      {/* Usage */}
                      <td className={`${cell} tabular-nums`}>
                        <span className={isDark ? "text-slate-200" : "text-slate-800"}>
                          {usagePct[r.name] != null ? fmt1(usagePct[r.name]) : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          
</section>

          {/* Build manager (chips + actions) */}
          {builds.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Builds:</span>

              <div className="flex flex-wrap gap-2">
                {builds
                  .slice()
                  .sort((a, b) => b.id - a.id)
                  .map((b) => {
                    const active = activeBuildId === b.id;
                    return (
                      <div
                        key={b.id}
                        className={cx(
                          "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm",
                          active
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        )}
                        title={new Date(b.ts).toLocaleString()}
                      >
                        <button
                          onClick={() => loadBuild(b.id)}
                          className="underline underline-offset-2"
                        >
                          {b.name}
                        </button>
                        <span className={cx("text-xs", active ? "text-white/80" : "text-gray-500")}>
                          {b.lineups?.length ?? 0}
                        </span>
                        <button
                          className={cx(
                            "ml-1 rounded-full w-4 h-4 flex items-center justify-center",
                            active ? "bg-white/20 hover:bg-white/30" : "bg-black/10 hover:bg-black/20"
                          )}
                          title="Delete build"
                          onClick={() => deleteBuild(b.id)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
              </div>

              <div className="flex items-center gap-2 ml-auto">
                {activeBuildId && (
                  <>
                    <button
                      className={ghostBtnClass}
                      onClick={() => {
                        const newName =
                          prompt(
                            "Rename build",
                            builds.find((x) => x.id === activeBuildId)?.name || ""
                          ) || "";
                        if (newName.trim()) renameBuild(activeBuildId, newName.trim());
                      }}
                    >
                      Rename
                    </button>
                    <button className={ghostBtnClass} onClick={() => deleteBuild(activeBuildId)}>
                      Delete
                    </button>
                  </>
                )}
                <button className={ghostBtnClass} onClick={clearBuilds}>
                  Clear All Builds
                </button>
              </div>
            </div>
          )}

{/* results & exposures */}

{!!lineups.length && (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
    {/* Lineups */}
    <section
      className={`lg:col-span-8 rounded-xl p-3 ${
        isDark
          ? "border border-slate-800 bg-slate-900/95"
          : "border border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className={`text-base font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
          Lineups ({lineups.length})
        </h2>

        <div className="flex items-center gap-2">
          {/* NFL-style: Export CSV = import template (IDs) */}
          <button
            className={`px-3 py-1.5 rounded text-sm border ${
              isDark
                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
            }`}
            onClick={() =>
              exportSiteTemplateCSV({
                lineups,
                site,
                rows: rowsWithBoost,
                siteIds: siteIds || {},
                siteMeta,
                slate,
                fname: `mlb_${site.toUpperCase()}_${slate}_ids.csv`,
              })
            }
          >
            Export CSV
          </button>

          {/* NFL-style: Detailed CSV = pretty/human-readable */}
          <button
            className={`px-3 py-1.5 rounded text-sm border ${
              isDark
                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50"
            }`}
            onClick={() =>
              exportDetailedLineupsCSV({
                lineups,
                site,
                rows: rowsWithBoost,
                siteIds: siteIds || {},
                siteMeta,
                slate,
                fname: `mlb_${site.toUpperCase()}_${slate}_detailed.csv`,
              })

              // If you haven't added exportDetailedLineupsCSV yet, use this temporarily:
              // exportLineupsCSV(lineups, rows, site, idMap)
            }
          >
            Detailed CSV
          </button>
        </div>
      </div>

      <div className="overflow-auto max-h-[440px] rounded-lg">
        <table
          className={`min-w-full text-[12px] border-separate ${
            isDark ? "text-slate-100" : "text-slate-900"
          }`}
          style={{ borderSpacing: 0 }}
        >
          <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
            <tr>
              <th className={`${header} ${isDark ? "text-slate-100" : ""}`}>#</th>
              <th className={`${header} ${isDark ? "text-slate-100" : ""}`}>Salary</th>
              <th className={`${header} ${isDark ? "text-slate-100" : ""}`}>
                {`Total pOWN%${
                  String(lineupPownCap).trim() !== "" ? ` (≤ ${lineupPownCap})` : ""
                }`}
              </th>
              <th className={`${header} ${isDark ? "text-slate-100" : ""}`}>
                Total {optBy === "pown" || optBy === "opt" ? "Projection" : metricLabel}
              </th>
              <th className={`${header} ${isDark ? "text-slate-100" : ""}`}>Players</th>
            </tr>
          </thead>

          <tbody>
            {lineups.map((L, i) => {
              const ordered = orderedRowsFromLineup(site, L, rowsByName);
              const totalPown = ordered.reduce((s, r) => s + (r.pown || 0) * 100, 0);
              const lineupProj = ordered.reduce((s, r) => s + (Number(r.proj) || 0), 0);

              return (
                <tr
                  key={i}
                  className={
                    isDark
                      ? "odd:bg-slate-950/70 even:bg-slate-900/70"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{i + 1}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(L.salary)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(totalPown)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(lineupProj || L.total)}</td>
                  <td className={`${cell} leading-snug`}>
                  <span className="break-words">
                    {ordered.map((r, idx) => {
                      const active = r.name === selectedExposurePlayer;
                      return (
                        <React.Fragment key={`${r.name}-${idx}`}>
                          <span
                            className={
                              active
                                ? isDark
                                  ? "bg-blue-500/25 text-blue-100 px-1 rounded font-semibold"
                                  : "bg-blue-100 text-blue-900 px-1 rounded font-semibold"
                                : ""
                            }
                          >
                            {r.name}
                          </span>
                          {idx < ordered.length - 1 ? " • " : ""}
                        </React.Fragment>
                      );
                    })}
                  </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>

              {/* Player Exposure */}
              <section
                className={`lg:col-span-4 rounded-xl p-3 ${
                  isDark
                    ? "border border-slate-800 bg-slate-900/95"
                    : "border border-slate-200 bg-white"
                }`}
              >
                  <div className="mb-2">
                    <h3 className={`text-base font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                      Exposure
                    </h3>

                    <div className="flex flex-wrap gap-2">
                      {["ALL","P","C","1B","2B","3B","SS","OF","UTIL"].map((p) => (
                        <button
                          key={p}
                          onClick={() => setExpPosFilter(p)}
                          className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                            expPosFilter === p
                              ? isDark
                                ? "bg-sky-500 text-slate-950 border-sky-400"
                                : "bg-blue-600 text-white border-blue-600"
                              : isDark
                                ? "bg-slate-950/40 text-slate-200 border-slate-700 hover:bg-slate-800"
                                : "bg-gray-100 text-slate-800 border-slate-200 hover:bg-gray-200"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                <ExposureTable
                  lineups={lineups}
                  rows={rowsWithBoost}
                  posFilter={expPosFilter}
                  isDark={isDark}
                  maxHeightClass="max-h-[440px]"
                  selectedPlayer={selectedExposurePlayer}
                  onSelectPlayer={(player) =>
                    setSelectedExposurePlayer((prev) => (prev === player ? null : player))
                  }
                />
              </section>

              {/* Team Exposure */}

              <section
  className={`lg:col-span-12 rounded-xl p-3 ${
    isDark
      ? "border border-slate-800 bg-slate-900/95"
      : "border border-slate-200 bg-white"
  }`}
>
  <button
    type="button"
    onClick={() => setShowBuildAnalysis((v) => !v)}
    className={
      "w-full flex items-center justify-between rounded-md px-2 py-2 text-left transition " +
      (isDark ? "hover:bg-slate-900/60" : "hover:bg-gray-50")
    }
  >
    <h3 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
      Build Analysis
    </h3>
    <span className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
      {showBuildAnalysis ? "Hide ▲" : "Show ▼"}
    </span>
  </button>

  {showBuildAnalysis && (
    <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Summary</h4>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Used</div>
          <div className="text-right tabular-nums">{fmt0(buildAnalysis.avgSalaryUsed)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Left</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgSalaryRemaining)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Projection</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgProjection)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Lineup pOWN%</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgLineupPown)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Geo Mean pOWN%</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgGeoMeanPown)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Stack Count</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgStackCount)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Primary Stack</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgPrimaryStackSize)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Secondary Stack</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgSecondaryStackSize)}</div>
        </div>

        <div className="mt-4">
          <h5 className={`text-xs font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Top Players</h5>
          <div className="overflow-auto max-h-[240px]">
            <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
                <tr>
                  <th className={header}>Player</th>
                  <th className={header}>Count</th>
                  <th className={header}>Exp %</th>
                </tr>
              </thead>
              <tbody>
                {buildAnalysis.topPlayers.map((r) => (
                  <tr
                    key={r.player}
                    className={
                      isDark
                        ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                        : "odd:bg-white even:bg-gray-50"
                    }
                  >
                    <td className={cell}>{r.player}</td>
                    <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                    <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <h5 className={`text-xs font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Top Primary Stack Teams</h5>
          <div className="overflow-auto max-h-[220px]">
            <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
                <tr>
                  <th className={header}>Team</th>
                  <th className={header}>Count</th>
                  <th className={header}>Build %</th>
                </tr>
              </thead>
              <tbody>
                {buildAnalysis.topPrimaryTeams.map((r) => (
                  <tr
                    key={r.team}
                    className={
                      isDark
                        ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                        : "odd:bg-white even:bg-gray-50"
                    }
                  >
                    <td className={cell}>
                      <span className="px-1.5 py-0.5 rounded text-[11px]" style={chipStyle(r.team)}>
                        {r.team}
                      </span>
                    </td>
                    <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                    <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Top 2-Player Pairings</h4>
        <div className="overflow-auto max-h-[300px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Pairing</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topPairs.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4 className={`text-sm font-semibold mt-4 mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Top 3-Player Pairings</h4>
        <div className="overflow-auto max-h-[300px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Pairing</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topTriples.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-slate-100" : "text-slate-900"}`}>Top Team Stack Combos</h4>
        <div className="overflow-auto max-h-[420px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Teams</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topTeamCombos.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )}
</section>
<section
  className={`lg:col-span-4 rounded-xl p-3 ${
    isDark
      ? "border border-slate-800 bg-slate-900/95"
      : "border border-slate-200 bg-white"
  }`}
>
  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
    <h3
      className={`text-base font-semibold ${
        isDark ? "text-slate-100" : "text-slate-900"
      }`}
    >
      Team Exposure
    </h3>

    <div className="flex items-center gap-2 flex-wrap">
      {[5, 4, 3, 2].map((sz) => (
        <button
          key={sz}
          type="button"
          onClick={() => setTeamExposureSize(sz)}
          className={`px-2 py-0.5 text-xs rounded border transition-colors ${
            teamExposureSize === sz
              ? isDark
                ? "bg-sky-500 text-slate-950 border-sky-400"
                : "bg-blue-600 text-white border-blue-600"
              : isDark
              ? "bg-slate-950/40 text-slate-200 border-slate-700 hover:bg-slate-800"
              : "bg-gray-100 text-slate-800 border-slate-200 hover:bg-gray-200"
          }`}
        >
          {sz}-man
        </button>
      ))}
    </div>
  </div>

  <TeamExposureTable
    lineups={lineups}
    rows={rowsWithBoost}
    isDark={isDark}
    maxHeightClass="max-h-[440px]"
    minHitters={Number(teamExposureSize) || 2}
  />
</section>

              {/* Stack Shapes */}
              <section
                className={`lg:col-span-4 rounded-xl p-3 ${
                  isDark
                    ? "border border-slate-800 bg-slate-900/95"
                    : "border border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                    Hitter Stack Shapes
                  </h3>
                </div>
                <StackShapesTable lineups={lineups} rows={rowsWithBoost} />
              </section>

              {/* Lineup Cards */}
              <section
                className={`lg:col-span-12 rounded-xl p-3 ${
                  isDark
                    ? "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
                    : "border border-slate-200 bg-white shadow-sm"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <h3 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                    Cards
                  </h3>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={isDark ? "text-slate-400 text-sm" : "text-gray-600 text-sm"}>
                      Sort lineups by:
                    </span>

                    <select
                      className={`border rounded-md px-2 py-1.5 text-sm ${inputClass}`}
                      value={cardsSortBy}
                      onChange={(e) => setCardsSortBy(e.target.value)}
                    >
                      <option value="proj">Projection</option>
                      <option value="salary">Salary</option>
                      <option value="pown">pOWN%</option>
                      <option value="geo">Geo Mean%</option>
                      <option value="opt">Opt%</option>
                    </select>

                    <button
                      type="button"
                      onClick={() => setCardsSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                      className={ghostBtnClass}
                      title="Toggle sort direction"
                    >
                      {cardsSortDir === "asc" ? "Asc ▲" : "Desc ▼"}
                    </button>
                  </div>
                </div>

                {/* AUTO-FITTING GRID — NEVER SQUISHES CARDS */}
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
                  }}
                >
                  {lineupsForCards.map((L, idx) => (
                    <LineupCard
                      key={idx}
                      idx={idx}
                      lineup={L}
                      rows={rowsWithBoost}
                      site={site}
                      total={L.total}
                      salary={L.salary}
                      isDark={isDark}
                      selectedExposurePlayer={selectedExposurePlayer}
                    />
                  ))}
                </div>
              </section>
            </div>
          )}
    </div>
  );
}

/* ---------------------- ordering helpers --------------------------- */
function orderPlayersForSite(site, names, rowsMap) {
  const pool = names.map((n) => rowsMap.get(n)).filter(Boolean);
  const take = (pred) => {
    const i = pool.findIndex(pred);
    if (i === -1) return null;
    return pool.splice(i, 1)[0];
  };
  const out = [];
  if (site === "dk") {
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("C")));
    out.push(take((r) => r.eligible.includes("1B")));
    out.push(take((r) => r.eligible.includes("2B")));
    out.push(take((r) => r.eligible.includes("3B")));
    out.push(take((r) => r.eligible.includes("SS")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
  } else {
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("C") || r.eligible.includes("1B")));
    out.push(take((r) => r.eligible.includes("2B")));
    out.push(take((r) => r.eligible.includes("3B")));
    out.push(take((r) => r.eligible.includes("SS")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => ["C","1B","2B","3B","SS","OF"].some(p => r.eligible.includes(p))));
  }
  return out.filter(Boolean).concat(pool);
}

function orderedRowsFromLineup(site, lineup, rowsMap) {
  const fallback = orderPlayersForSite(site, lineup?.players || [], rowsMap);

  if (!Array.isArray(lineup?.assignment) || !lineup.assignment.length) {
    return fallback;
  }

  const getAssigned = (slot) => {
    const a = lineup.assignment.find((x) => x?.slot === slot);
    return a?.name ? rowsMap.get(a.name) || null : null;
  };

  // ============================================================
  // DRAFTKINGS
  //
  // Keep every position exactly where the solver put it.
  // Only sort OF1 / OF2 / OF3 by salary high -> low.
  // ============================================================
  if (site === "dk") {
    const pitchers = [
      getAssigned("P1"),
      getAssigned("P2"),
    ];

    const fixedHitters = [
      getAssigned("C"),
      getAssigned("1B"),
      getAssigned("2B"),
      getAssigned("3B"),
      getAssigned("SS"),
    ];

    const outfielders = [
      getAssigned("OF1"),
      getAssigned("OF2"),
      getAssigned("OF3"),
    ]
      .filter(Boolean)
      .sort(
        (a, b) =>
          (Number(b.salary) || 0) - (Number(a.salary) || 0)
      );

    return [
      ...pitchers,
      ...fixedHitters,
      ...outfielders,
    ].filter(Boolean);
  }

  // ============================================================
  // FANDUEL
  //
  // Treat UTIL like NFL FLEX:
  //
  // 1. Pick the latest-starting hitter for UTIL.
  // 2. If start times tie, higher salary gets UTIL.
  // 3. Reassign the remaining hitters to legal FD positions.
  // 4. Sort the final OF spots by salary high -> low.
  // ============================================================

  const pitcher = getAssigned("P");

  const hitters = [
    getAssigned("C/1B"),
    getAssigned("2B"),
    getAssigned("3B"),
    getAssigned("SS"),
    getAssigned("OF1"),
    getAssigned("OF2"),
    getAssigned("OF3"),
    getAssigned("UTIL"),
  ].filter(Boolean);

  // ------------------------------------------------------------
  // Find which hitters could legally be moved to UTIL while still
  // allowing the other 7 hitters to fill the normal FD positions.
  // ------------------------------------------------------------
  const canFillNormalSlots = (players) => {
    const slots = [
      ["C", "1B"],
      ["2B"],
      ["3B"],
      ["SS"],
      ["OF"],
      ["OF"],
      ["OF"],
    ];

    const used = new Set();

    const search = (slotIndex) => {
      if (slotIndex >= slots.length) return true;

      const eligiblePositions = slots[slotIndex];

      for (let i = 0; i < players.length; i++) {
        if (used.has(i)) continue;

        const r = players[i];
        const eligible = Array.isArray(r?.eligible) ? r.eligible : [];

        const fits = eligiblePositions.some((pos) =>
          eligible.includes(pos)
        );

        if (!fits) continue;

        used.add(i);

        if (search(slotIndex + 1)) return true;

        used.delete(i);
      }

      return false;
    };

    return search(0);
  };

  const utilCandidates = hitters.filter((candidate) => {
    const remainingHitters = hitters.filter((r) => r !== candidate);
    return canFillNormalSlots(remainingHitters);
  });

  utilCandidates.sort((a, b) => {
    const timeA = parseMlbTimeToMinutes(a.time);
    const timeB = parseMlbTimeToMinutes(b.time);

    const validA = Number.isFinite(timeA);
    const validB = Number.isFinite(timeB);

    // A known start time beats an unknown one
    if (validA && !validB) return -1;
    if (!validA && validB) return 1;

    // Latest game gets UTIL
    if (validA && validB && timeA !== timeB) {
      return timeB - timeA;
    }

    // Same game time -> higher salary gets UTIL
    return (Number(b.salary) || 0) - (Number(a.salary) || 0);
  });

  const utilPlayer =
    utilCandidates[0] ||
    getAssigned("UTIL") ||
    null;

  const remaining = hitters.filter((r) => r !== utilPlayer);

  // ------------------------------------------------------------
  // Assign the remaining hitters into legal normal FD slots.
  // Uses backtracking so multi-position players stay legal.
  // ------------------------------------------------------------
  const normalSlots = [
    { name: "C/1B", eligible: ["C", "1B"] },
    { name: "2B", eligible: ["2B"] },
    { name: "3B", eligible: ["3B"] },
    { name: "SS", eligible: ["SS"] },
    { name: "OF1", eligible: ["OF"] },
    { name: "OF2", eligible: ["OF"] },
    { name: "OF3", eligible: ["OF"] },
  ];

  const assignments = new Array(normalSlots.length).fill(null);
  const used = new Set();

  const assignSlots = (slotIndex) => {
    if (slotIndex >= normalSlots.length) return true;

    const slot = normalSlots[slotIndex];

    // For OF spots, prefer higher salary first
    const candidateIndexes = remaining
      .map((r, index) => ({ r, index }))
      .filter(({ r, index }) => {
        if (used.has(index)) return false;

        const eligible = Array.isArray(r?.eligible) ? r.eligible : [];

        return slot.eligible.some((pos) =>
          eligible.includes(pos)
        );
      })
      .sort((a, b) => {
        if (slot.eligible.includes("OF")) {
          return (
            (Number(b.r.salary) || 0) -
            (Number(a.r.salary) || 0)
          );
        }

        return 0;
      });

    for (const { r, index } of candidateIndexes) {
      used.add(index);
      assignments[slotIndex] = r;

      if (assignSlots(slotIndex + 1)) {
        return true;
      }

      used.delete(index);
      assignments[slotIndex] = null;
    }

    return false;
  };

  const success = assignSlots(0);

  // Safety fallback: if something unexpected happens,
  // use the original solver assignment instead of breaking the lineup.
  if (!success) {
    const slotOrder = [
      "P",
      "C/1B",
      "2B",
      "3B",
      "SS",
      "OF1",
      "OF2",
      "OF3",
      "UTIL",
    ];

    return slotOrder
      .map((slot) => getAssigned(slot))
      .filter(Boolean);
  }

  return [
    pitcher,
    ...assignments,
    utilPlayer,
  ].filter(Boolean);
}

/* --------------------------- Cards --------------------------- */
function LineupCard({ idx, lineup, rows, site, total, salary, isDark = false, selectedExposurePlayer = null }) {
  const rowsMap = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);
const ordered = orderedRowsFromLineup(site, lineup, rowsMap);
const stackStr = getStackString(lineup?.players || [], rowsMap);

  const slotLabels =
    site === "dk"
      ? ["P", "P", "C", "1B", "2B", "3B", "SS", "OF", "OF", "OF"]
      : ["P", "C/1B", "2B", "3B", "SS", "OF", "OF", "OF", "UTIL"];

// Sum of player pOWN% (displayed as percent)
const totalPown = ordered.reduce((s, r) => s + ((r.pown || 0) * 100), 0);
const geoMeanPown = geoMeanPct(ordered, (r) => (r.pown || 0) * 100);
const displayedTotalProj = ordered.reduce((s, r) => s + (Number(r.proj) || 0), 0);

  // Helper to get batting order for hitters (dash for pitchers)
  const getBattingOrder = (r) => {
    if (r.isPitcher) return "—";
    const raw = r.__raw || {};
    const bo =
      raw.battingOrder ??
      raw.BattingOrder ??
      raw.bo ??
      raw.BO ??
      raw.lineupOrder ??
      raw.LineupOrder ??
      raw.order ??
      raw.Order ??
      raw.batting_order ??
      raw.LineupSpot ??
      raw.spot;
    return bo ?? "—";
  };

  // Compact salary label (keeps column from clipping)
  const fmtSal = (n) => {
    const x = Number(n) || 0;
    if (!Number.isFinite(x)) return "—";
    if (x >= 1000) return `${Math.round(x / 100) / 10}k`; // 9500 -> 9.5k
    return String(x);
  };

  // Logo path helper (adjust if your repo uses a different path)
  const teamLogoSrc = (abbr) => {
    if (!abbr) return null;
    const t = String(abbr).toLowerCase();
    return `/logos/mlb/${t}.png`;
  };

  const Card = isDark
    ? "border border-slate-800 bg-slate-900/95 shadow-[0_18px_50px_rgba(0,0,0,0.55)]"
    : "border border-slate-200 bg-white shadow-sm";

  const HeaderBg = isDark ? "bg-slate-950/90" : "bg-slate-100";
  const RowOdd = isDark ? "bg-slate-900/70" : "bg-white";
  const RowEven = isDark ? "bg-slate-950/60" : "bg-slate-50";
  const RowHover = isDark ? "hover:bg-slate-800/70" : "hover:bg-blue-50/70";
  const TextMain = isDark ? "text-slate-100" : "text-slate-900";
  const TextSub = isDark ? "text-slate-400" : "text-slate-600";
  const Border = isDark ? "border-slate-800" : "border-slate-200";

  // Let logo shrink on mobile + slightly smaller on mobile to buy space
  const TeamLogoInline = ({ team }) => {
    if (!team) return null;
    const src = teamLogoSrc(team);
    return (
      <img
        src={src}
        alt={team}
        title={team}
        className="w-4 h-4 sm:w-5 sm:h-5 object-contain shrink-0"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  };

  return (
    <div className={`rounded-xl p-3 min-w-0 ${Card}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
        <div className={`font-semibold ${TextMain}`}>Lineup #{idx + 1}</div>
        <div className={`text-xs ${TextSub}`}>
          Stack {stackStr}
        </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-[10px] font-medium ${TextSub}`} title="Geometric Mean pOWN%">
            Geo Mean% {fmt1(geoMeanPown)}
          </span>
          <img
            src={SITES[site].logo}
            alt=""
            className="w-5 h-5 opacity-80 shrink-0"
            title={SITES[site].label}
          />
        </div>
      </div>

      {/* Table */}
      <div className={`overflow-hidden rounded-lg border ${Border}`}>
        {/* table-fixed + mobile-tight padding/fonts + narrower fixed cols */}
        <table className={`w-full table-fixed ${TextMain}`}>
          <thead className={`${HeaderBg} border-y ${Border}`}>
            <tr className="text-[10px] sm:text-[12px]">
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-left w-[26px] sm:w-[42px]">
                Slot
              </th>
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-center w-[20px] sm:w-[30px]">
                BO
              </th>
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-left">
                Player
              </th>
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-right w-[40px] sm:w-[56px]">
                pOWN%
              </th>
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-right w-[40px] sm:w-[46px]">
                Proj
              </th>
              <th className="py-[2px] sm:py-1 px-1 sm:px-2 text-right w-[38px] sm:w-[44px]">
                Sal
              </th>
            </tr>
          </thead>

          <tbody>
            {ordered.map((r, i) => (
              <tr
                key={`${r.name}-${i}`}
                className={`transition-colors ${i % 2 === 0 ? RowOdd : RowEven} ${RowHover}`}
              >
                <td className={`py-[2px] sm:py-1 px-1 sm:px-2 text-[10px] sm:text-[12px] ${TextSub}`}>
                  {slotLabels[i] || "—"}
                </td>

                <td className={`py-[2px] sm:py-1 px-1 sm:px-2 text-center text-[10px] sm:text-[12px] ${TextSub}`}>
                  {getBattingOrder(r)}
                </td>

                {/* Player */}
                <td className="py-[2px] sm:py-1 px-1 sm:px-2 min-w-0">
                  <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                    <TeamLogoInline team={r.team} />
                    <span
                      className={
                        "truncate font-medium text-[10px] sm:text-[12px] " +
                        (r.name === selectedExposurePlayer
                          ? isDark
                            ? "bg-blue-500/25 text-blue-100 px-1 rounded"
                            : "bg-blue-100 text-blue-900 px-1 rounded"
                          : "")
                      }
                    >
                      {r.name}
                    </span>
                  </div>
                </td>

                <td className={`py-[2px] sm:py-1 px-1 sm:px-2 text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px] ${TextSub}`}>
                  {fmt1((r.pown || 0) * 100)}
                </td>
                <td
                  className={`py-[2px] sm:py-1 px-1 sm:px-2 text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px] ${
                    r.hasCustomProj && Number(r.proj) > Number(r.baseProj || 0)
                      ? isDark ? "text-emerald-300 font-semibold" : "text-emerald-700 font-semibold"
                      : r.hasCustomProj && Number(r.proj) < Number(r.baseProj || 0)
                      ? isDark ? "text-red-300 font-semibold" : "text-red-700 font-semibold"
                      : TextSub
                  }`}
                  title={r.hasCustomProj ? `Original projection: ${fmt1(r.baseProj || 0)}` : undefined}
                >
                  {fmt1(r.proj)}
                </td>
                <td className={`py-[2px] sm:py-1 px-1 sm:px-2 text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px] ${TextSub}`}>
                  {fmtSal(r.salary)}
                </td>
              </tr>
            ))}

            {/* Totals row */}
            <tr className={`border-t ${Border} ${isDark ? "bg-slate-950/70" : "bg-gray-50"}`}>
              <td className="py-[2px] sm:py-1 px-1 sm:px-2 font-semibold text-left text-[10px] sm:text-[12px]">
                Totals
              </td>
              <td className="py-[2px] sm:py-1 px-1 sm:px-2" />
              <td className="py-[2px] sm:py-1 px-1 sm:px-2" />
              <td className="py-[2px] sm:py-1 px-1 sm:px-2 font-semibold text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px]">
                {fmt1(totalPown)}
              </td>
              <td className="py-[2px] sm:py-1 px-1 sm:px-2 font-semibold text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px]">
                {fmt1(displayedTotalProj || total)}
              </td>
              <td className="py-[2px] sm:py-1 px-1 sm:px-2 font-semibold text-right whitespace-nowrap tabular-nums text-[10px] sm:text-[12px]">
                {fmtSal(salary)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------- Exposure tables --------------------------- */
function ExposureTable({
  lineups,
  rows,
  posFilter = "ALL",
  isDark = false,
  maxHeightClass = "max-h-[440px]",
  selectedPlayer = null,
  onSelectPlayer = null,
}) {
  const meta = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);

  const expandEligible = (r) => {
    const set = new Set();

    if (r?.isPitcher) set.add("P");

    const elig = Array.isArray(r?.eligible) ? r.eligible : [];
    for (const token of elig) {
      if (!token) continue;

      const parts = String(token)
        .split("/")
        .map((s) => s.trim())
        .filter(Boolean);

      for (const p of parts) set.add(p);
    }

    if (set.has("SP") || set.has("RP")) set.add("P");

    return set;
  };

  const allRows = useMemo(() => {
    const counts = new Map();

    for (const L of lineups || []) {
      for (const name of L.players || []) {
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    }

    const total = Math.max(1, lineups.length);

    let arr = Array.from(counts.entries()).map(([name, cnt]) => {
      const r = meta.get(name);
      const eligSet = expandEligible(r);
      const displayPos = Array.isArray(r?.eligible) ? r.eligible.join("/") : "?";

      return {
        name,
        count: cnt,
        pct: (cnt / total) * 100,
        pos: displayPos,
        eligSet,
        isPitcher: !!r?.isPitcher || eligSet.has("P"),
      };
    });

    if (posFilter !== "ALL") {
      if (posFilter === "UTIL") {
        arr = arr.filter((x) => !x.isPitcher);
      } else {
        arr = arr.filter((x) => x.eligSet.has(posFilter));
      }
    }

    return arr.sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
  }, [lineups, meta, posFilter]);

  if (!allRows.length) return null;

  const cell = `px-2 py-1 text-center ${isDark ? "text-slate-100" : "text-slate-900"}`;
  const header = `px-2 py-1 font-semibold text-center ${isDark ? "text-slate-100" : "text-slate-900"}`;

  return (
    <div
      className={`overflow-auto ${maxHeightClass} rounded-lg border ${
        isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-white"
      }`}
    >
      <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
        <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
          <tr>
            <th className={header}>Player</th>
            <th className={header}>Pos</th>
            <th className={header}>Count</th>
            <th className={header}>Exposure %</th>
          </tr>
        </thead>
        <tbody>
          {allRows.map((r) => {
            const active = selectedPlayer === r.name;

            return (
              <tr
                key={r.name}
                onClick={() => onSelectPlayer?.(r.name)}
                className={
                  "cursor-pointer transition-colors " +
                  (isDark
                    ? active
                      ? "bg-blue-600/25"
                      : "odd:bg-slate-950/80 even:bg-slate-900/80 hover:bg-slate-800/80"
                    : active
                    ? "bg-blue-100"
                    : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/70")
                }
              >
                <td className={cell}>{r.name}</td>
                <td className={cell}>{r.pos}</td>
                <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TeamExposureTable({
  lineups,
  rows,
  isDark = false,
  maxHeightClass = "max-h-[440px]",
  minHitters = 2,
}) {
  const rowsByName = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);

  const data = useMemo(() => {
    const counts = new Map();

    for (const L of lineups || []) {
      const hitterCounts = new Map();

      for (const name of L.players || []) {
        const r = rowsByName.get(name);
        if (!r) continue;
        if (r.isPitcher) continue;
        if (!r.team) continue;

        hitterCounts.set(r.team, (hitterCounts.get(r.team) || 0) + 1);
      }

      for (const [team, cnt] of hitterCounts.entries()) {
        if (cnt === minHitters) {
          counts.set(team, (counts.get(team) || 0) + 1);
        }
      }
    }

    const total = Math.max(1, lineups?.length || 0);

    return [...counts.entries()]
      .map(([team, cnt]) => ({
        team,
        count: cnt,
        pct: (cnt / total) * 100,
      }))
      .sort((a, b) => b.pct - a.pct || a.team.localeCompare(b.team));
  }, [lineups, rowsByName, minHitters]);

  if (!data.length) {
    return (
      <div
        className={`flex items-center justify-center ${maxHeightClass} rounded-lg border ${
          isDark
            ? "border-slate-800 bg-slate-950/40 text-slate-400"
            : "border-slate-200 bg-white text-slate-500"
        }`}
      >
        No {minHitters}-man team stacks found
      </div>
    );
  }

  const cell = `px-2 py-1 text-center ${isDark ? "text-slate-100" : "text-slate-900"}`;
  const header = `px-2 py-1 font-semibold text-center ${isDark ? "text-slate-100" : "text-slate-900"}`;

  return (
    <div
      className={`overflow-auto ${maxHeightClass} rounded-lg border ${
        isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-white"
      }`}
    >
      <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
        <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
          <tr>
            <th className={header}>Team</th>
            <th className={header}>Count</th>
            <th className={header}>{minHitters}-man %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => (
            <tr
              key={r.team}
              className={
                isDark
                  ? "odd:bg-slate-950/80 even:bg-slate-900/80 hover:bg-slate-800/80"
                  : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/70"
              }
            >
              <td className={cell}>
                <span className="px-1.5 py-0.5 rounded text-[11px]" style={chipStyle(r.team)}>
                  {r.team}
                </span>
              </td>
              <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
              <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StackShapesTable({ lineups, rows }) {
  const rowsByName = useMemo(() => new Map(rows.map(r => [r.name, r])), [rows]);
  const data = useMemo(() => {
    const counts = {};
    for (const L of lineups) {
      const chosen = L.players.map((n) => rowsByName.get(n)).filter(Boolean);
      const hitters = chosen.filter(r => !r.isPitcher);
      const byTeam = new Map();
      for (const h of hitters) byTeam.set(h.team, (byTeam.get(h.team) || 0) + 1);
      const sizes = [...byTeam.values()].sort((a,b)=>b-a);
      const label = sizes.join("-") || "—";
      counts[label] = (counts[label] || 0) + 1;
    }
    const total = Math.max(1, lineups.length);
    return Object.entries(counts).map(([shape, cnt]) => ({ shape, count: cnt, pct: (cnt / total) * 100 }))
      .sort((a,b)=> b.count - a.count || a.shape.localeCompare(b.shape));
  }, [lineups, rowsByName]);

  if (!data.length) return null;

  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";

  return (
    <table className="min-w-full text-[12px]">
      <thead><tr><th className={header}>Shape</th><th className={header}>Count</th><th className={header}>%</th></tr></thead>
      <tbody>{data.map(r => (
        <tr key={r.shape}><td className={cell}>{r.shape}</td><td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td><td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td></tr>
      ))}</tbody>
    </table>
  );
}
